import { flowDefaultAction, flowEventRules, deriveGraph, deriveTaskEvents, MAX_BRANCH_COL, MAX_POINTS, type EdgeCategory, type GraphEdge, type TaskEvent } from '@agavi/blocks';
import { DndContext, MouseSensor, TouchSensor, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, Ban, Check, ChevronDown, ChevronRight, ChevronUp, CircleDashed, ClipboardPaste, Copy, CopyPlus, CornerDownRight, Flag, Footprints, GripVertical, Info, MoreHorizontal, OctagonAlert, Plus, Star, Trash2, TrendingDown, TriangleAlert, Unlink, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { computeLayout, type Placed } from './builder-layout';

import type { Block, ScoringMode, Task } from '@/types/experience';

import { BLOCK_META } from '@/components/task/block-library';
import { POPUP_LEVEL } from '@/components/ui/popup-level';
import { useCapability } from '@/hooks/use-capabilities';
import { commitScore, formatScore, sanitizeScoreDraft } from '@/lib/score-input';

/** Display name for a task event: built-ins are named from `kind` (the flow util is i18n-free);
 * a special event carries its teacher-authored title. */
function eventLabel(ev: TaskEvent, t: (k: string) => string): string {
  switch (ev.kind) {
    case 'special': return ev.title ?? '';
    case 'incorrect': return t('task.flow.defaultRung.title');
    case 'noAnswer': return t('task.flow.anyIncomplete.title');
    default: return t('task.flow.allCorrect.title');
  }
}

/** How a wire terminates — the event category, plus `special` broken out so a Special-event wire can
 * carry the star (its category is `neutral`, but it deserves the port's star glyph, not the ring). */
type EdgeTerm = TaskEvent['category'] | 'special';

/**
 * EdgeTerm → connector stroke colour + terminator marker (see the <marker> defs). Matches the port
 * dots and the minimap's own caps: route = teal arrow, fallback = the same arrow hollow, gate =
 * muted dashed ring, sensor = amber barred ring, special = link-blue star.
 *
 * SHAPE carries the family and colour only reinforces it: gate and fallback are both muted, and a
 * reader who cannot separate two greys still separates a ring from an arrow.
 */
const CAT_STROKE: Record<EdgeTerm, string> = {
  route: 'var(--color-ws-primary)',
  // Not error red. Default means "nothing above matched", which is not a verdict on the student.
  fallback: 'var(--color-ws-muted)',
  gate: 'var(--color-ws-muted)',
  sensor: 'var(--color-ws-warning-fg)',
  special: 'var(--color-ws-text-link)',
};
const CAT_MARKER: Record<EdgeTerm, string> = {
  route: 'url(#sb-route)',
  fallback: 'url(#sb-fallback)',
  gate: 'url(#sb-gate)',
  sensor: 'url(#sb-sensor)',
  special: 'url(#sb-special)',
};

/** Glyph for each of the four flow-event TYPES (used in the port list + arming banner). The events
 * themselves aren't "stop/go" (that's what the wire terminators convey) — these name the event:
 * correct = check, incorrect = ✕, incomplete/no-answer = dashed ring, special = star. */
function EventSymbol({ kind }: { kind: TaskEvent['kind'] }) {
  const cls = 'h-3.5 w-3.5 shrink-0';
  switch (kind) {
    case 'correct': return <Check className={`${cls} text-ws-primary`} aria-hidden />;
    case 'incorrect': return <X className={`${cls} text-ws-error-fg`} aria-hidden />;
    case 'special': return <Star className={`${cls} text-ws-text-link`} aria-hidden />;
    default: return <CircleDashed className={`${cls} text-ws-muted`} aria-hidden />; // noAnswer = incomplete
  }
}

// Display blocks are context (muted in the composition strip); everything else is an answer/input
// or sensor block (emphasised) — see the block library's own categories.
const DISPLAY_TYPES = new Set(['text', 'image', 'sound', 'dataviz']);

/** Compact fingerprint of a task's blocks: distinct block types in first-appearance order, so a
 * task with five text blocks still reads as one Text icon and complex tasks never overflow. Answer/
 * input/sensor blocks are emphasised (primary), display blocks muted — the "context → question" read. */
/** What the errors aspect needs to know about one task: which of its blocks are flagged, and how
 *  loudly the task as a whole speaks. Built by the page from `experienceProblems`. */
export interface TaskProblemSummary {
  blockErrors: Set<string>;
  blockWarnings: Set<string>;
  errors: number;
  warnings: number;
}

/**
 * ONE CHIP PER BLOCK, for the errors aspect — which is why the aspect swaps the body rather than
 * marking `BlockStrip`.
 *
 * `BlockStrip` deduplicates by TYPE: a task with three MCQs shows one MCQ icon. Marking that icon
 * says "an MCQ here has a problem" and never which, so it cannot be a target for click-to-go-to
 * either. This draws every block in document order, so a mark on the board and a row in the panel
 * point at the same thing.
 *
 * The glyph is the mark, not the tint — tint is never the only marker, and at this size a colour
 * alone would also be the only difference between an error and a warning.
 */
function ProblemStrip({ blocks, problems, max, highlightedBlockId, onOpenBlock }: {
  blocks?: Block[];
  problems?: TaskProblemSummary;
  /** How many chips to draw, from the same `chipPlan` the card's height was computed from — so the
   *  strip never draws a row the card was not grown for. */
  max: number;
  /** The block the panel's hovered row names, lit so a reader can see WHICH one it means. */
  highlightedBlockId?: string;
  onOpenBlock?: (blockId: string) => void;
}) {
  const { t } = useTranslation();
  if (!blocks?.length) return null;
  const flagged = blocks.filter((b) => problems?.blockErrors.has(b.id) || problems?.blockWarnings.has(b.id));
  const rest = blocks.filter((b) => !flagged.includes(b));
  const shown = [...flagged, ...rest].slice(0, Math.max(1, max));
  const inOrder = blocks.filter((b) => shown.includes(b));
  const extra = blocks.length - inOrder.length;

  return (
    /* NOT `overflow-hidden`, which the one-row BlockStrip below can afford and this cannot: every
       chip here wears a mark badge hanging 4px past its corner and, when the panel points at it, a
       ring sitting 3px outside that. Clipped, the fault symbol lost its bottom half and the
       highlight drew as three sides of a box. The card's own `py-2` is the room they spill into. */
    <div className='flex flex-wrap items-center gap-1.5 pl-7'>
      {inOrder.map((b) => {
        const meta = BLOCK_META[b.type];
        if (!meta) return null;
        const Icon = meta.Icon;
        const isError = problems?.blockErrors.has(b.id);
        const isWarning = !isError && problems?.blockWarnings.has(b.id);
        const Mark = isError ? OctagonAlert : AlertTriangle;
        const tone = isError
          ? 'border-ws-error-fg bg-ws-error-bg text-ws-error-fg'
          : isWarning ? 'border-ws-warning-fg bg-ws-warning-bg text-ws-warning-fg'
          : 'border-ws-border bg-ws-surface text-ws-muted';
        const lit = b.id === highlightedBlockId;
        const name = b.name ?? (meta ? t(meta.labelKey) : b.type);
        /* A REAL BUTTON, because it does something: the owner asked whether the icons go to the
           problem block, and they should — a mark that says "here" and cannot be followed is half
           an affordance. `stopPropagation` so it opens THAT block rather than the card's own
           activate, which would open the task on its first block. */
        return (
          <button key={b.id} type='button'
            onClick={(e) => { e.stopPropagation(); onOpenBlock?.(b.id); }}
            aria-label={t(isError ? 'experience.problems.blockError'
              : isWarning ? 'experience.problems.blockWarning'
              : 'experience.problems.blockClean', { block: name })}
            title={name}
            className={`relative flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-md border transition-shadow ${tone} ${
              lit ? 'ring-2 ring-ws-primary ring-offset-1' : ''}`}>
            <Icon aria-hidden='true' className='h-[15px] w-[15px]' />
            {(isError || isWarning) && (
              <span aria-hidden='true' className='absolute -bottom-1 -right-1 flex h-[13px] w-[13px] items-center justify-center rounded-full bg-ws-surface'>
                <Mark className='h-[11px] w-[11px]' strokeWidth={2.4} />
              </span>
            )}
          </button>
        );
      })}
      {extra > 0 && <span className='shrink-0 text-[11px] font-semibold text-ws-muted/70'>+{extra}</span>}
    </div>
  );
}

function BlockStrip({ blocks }: { blocks?: Block[] }) {
  const { t } = useTranslation();
  if (!blocks?.length) return null;
  const seen = new Set<string>();
  const types: string[] = [];
  for (const b of blocks) { if (b.type && !seen.has(b.type)) { seen.add(b.type); types.push(b.type); } }
  if (!types.length) return null;
  const label = types.map((ty) => (BLOCK_META[ty] ? t(BLOCK_META[ty].labelKey) : ty)).join(', ');
  // Keep the fingerprint to a single legible row: show the first few block types full-size, then a
  // "+N" for the rest (wrapping to multiple rows made a many-block card tall and cluttered).
  const MAX = 6;
  const shown = types.length > MAX ? types.slice(0, MAX - 1) : types;
  const extra = types.length - shown.length;
  return (
    <div className='flex items-center gap-x-1.5 overflow-hidden pl-7' role='img' aria-label={t('experience.builder.contains', { list: label })}>
      {shown.map((ty) => {
        const meta = BLOCK_META[ty];
        if (!meta) return null;
        const Icon = meta.Icon;
        const emphasised = !DISPLAY_TYPES.has(ty);
        return <Icon key={ty} className={`h-[18px] w-[18px] shrink-0 ${emphasised ? 'text-ws-primary' : 'text-ws-muted/60'}`} />;
      })}
      {extra > 0 && <span className='shrink-0 pl-0.5 text-[11px] font-semibold text-ws-muted/70'>+{extra}</span>}
    </div>
  );
}

/** A task's effective scoring mode — from `scoringMode`, or inferred from points/penalty for a legacy
 * task that predates the field (points+penalty ⇒ penalty, points ⇒ milestone, else ungraded). */
function inferScoringMode(task: Task): ScoringMode {
  if (task.scoringMode) return task.scoringMode;
  if ((task.points ?? 0) > 0) return (task.penalty ?? 0) > 0 ? 'penalty' : 'milestone';
  return 'ungraded';
}

/** One symbol per scoring mode, shared by the card cycler and the default strip's selector:
 * ungraded = Ban (not scored), milestone = Flag (checkpoint), penalty = TrendingDown (points decay). */
const SCORING_MODE_ICON: Record<ScoringMode, typeof Ban> = { ungraded: Ban, milestone: Flag, penalty: TrendingDown };

/** Compact numeric input for the on-card scoring body. Digits-only, commits on blur/Enter, clamps to
 * `min`; a penalty (`negative`) shows a leading −. Stops propagation so clicks never open the card
 * editor or start a drag. */
function ScoringMini({ label, value, onChange, disabled, min = 0, max = MAX_POINTS, negative, coarse }: { label: string; value: number; onChange: (v: number) => void; disabled?: boolean; min?: number; max?: number; negative?: boolean; coarse?: boolean }) {
  const [draft, setDraft] = useState(formatScore(value));
  useEffect(() => setDraft(formatScore(value)), [value]);
  const commit = () => {
    const v = commitScore(draft, value, { min, max });
    setDraft(formatScore(v));
    if (v !== value) onChange(v);
  };
  return (
    <label className='flex flex-col items-center gap-0.5'>
      <span className='text-[10px] font-medium leading-4 text-ws-muted'>{label}</span>
      <span className={`flex items-center rounded-md border border-ws-border bg-ws-surface focus-within:border-ws-primary ${disabled ? 'opacity-40' : ''}`}>
        {negative && <span aria-hidden='true' className={`text-ws-muted ${coarse ? 'pl-2' : 'pl-1.5'}`}>−</span>}
        {/* inputMode=numeric + pattern → the numeric keypad on touch, not the full keyboard. */}
        <input
          type='text' inputMode='decimal' pattern='[0-9]*[.,]?[0-9]*' value={draft} disabled={disabled}
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(sanitizeScoreDraft(e.target.value))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className={`border-0 bg-transparent text-center leading-none tabular-nums text-ws-text focus:outline-none ${coarse ? 'h-9 w-12 text-base' : 'h-7 w-10 text-sm'} ${negative ? 'pl-0.5 pr-2' : 'px-1.5'}`}
        />
      </span>
    </label>
  );
}

/** The card body shown in the scoring aspect: a 3-way mode selector + points / step-down steppers,
 * replacing the block-composition strip.
 *
 * NOT `data-noedit` any more. That marked the whole strip as "clicks here do nothing", which was
 * harmless while a click in this aspect did nothing anyway — and became the reason clicking a card
 * to pick it silently failed, since the strip IS most of the card here. The controls inside it are
 * buttons and inputs, which the card's own click guard already skips, so the blanket marker was
 * doing no work the specific check does not. */
function CardScoringBody({ task, readOnly, coarse, onSet }: { task: Task; readOnly: boolean; coarse?: boolean; onSet: (p: { scoringMode: ScoringMode; points: number; penalty: number }) => void }) {
  const { t } = useTranslation();
  const mode = inferScoringMode(task);
  const points = task.points ?? 0;
  const step = task.penalty ?? 0;
  const modes: ScoringMode[] = ['ungraded', 'milestone', 'penalty'];
  const setMode = (m: ScoringMode) => onSet({ scoringMode: m, points: m === 'ungraded' ? 0 : (points || 1), penalty: m === 'penalty' ? (step || 1) : 0 });
  const modeLabel = t(`experience.scoring.mode.${mode}`);
  const ModeIcon = SCORING_MODE_ICON[mode];
  const ungraded = mode === 'ungraded';
  // Same bordered icon-button style as the flow/feedback action cycler; click cycles the mode.
  const cyclerBtn = (
    <button type='button' disabled={readOnly}
      onClick={(e) => { e.stopPropagation(); setMode(modes[(modes.indexOf(mode) + 1) % modes.length]!); }}
      aria-label={t('experience.scoring.cycleMode', { mode: modeLabel })}
      title={t('experience.scoring.cycleMode', { mode: modeLabel })}
      className={`mt-0.5 flex items-center justify-center rounded-md border border-ws-border-strong text-ws-text transition-colors hover:bg-ws-hover disabled:opacity-40 ${coarse ? 'h-12 w-12' : 'h-11 w-11'}`}>
      <ModeIcon aria-hidden='true' className={coarse ? 'h-6 w-6' : 'h-5 w-5'} />
    </button>
  );
  // Centre the cycler against the field container(s) — the fixed min-height keeps the body from
  // reflowing as you cycle modes.
  return (
    <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-7 pr-2' style={{ minHeight: coarse ? 56 : 48 }}>
      {cyclerBtn}
      {ungraded ? (
        <span className='text-[11px] italic text-ws-muted/70'>{t('experience.scoring.noPoints')}</span>
      ) : (
        <>
          <ScoringMini label={t('experience.scoring.points')} value={points} min={1} coarse={coarse} disabled={readOnly}
            // A penalty may never exceed the points, so lowering the points brings it down too.
            onChange={(v) => onSet({ scoringMode: mode, points: v, penalty: mode === 'penalty' ? Math.min(step, v) : 0 })} />
          {mode === 'penalty' && (
            <ScoringMini label={t('experience.scoring.stepDown')} value={step} min={1} max={points} negative coarse={coarse} disabled={readOnly}
              onChange={(v) => onSet({ scoringMode: mode, points, penalty: v })} />
          )}
        </>
      )}
    </div>
  );
}

/** The pathway (remediation pathway) bar — a distinct amber band, NOT a task card, so a collapsed
 * pathway can never be mistaken for a task. One component for both states: the name field and the
 * collapse chevron sit in a fixed layout with the chevron pinned RIGHT (mirroring the flow feedback
 * card) so it never jumps as you collapse/expand. Rendered as an overlay above the pathway's head
 * when expanded, or in the head's (short) slot when collapsed. The name stays lighter than a task
 * title so the pathway reads as a container, not a competing card. Name commits on blur/Enter (not
 * per-keystroke) so it doesn't spam the undo stack. */
// Shared accessible ⋯ menu for a card or a pathway bar: Duplicate + a two-click-armed Delete (the
// app-wide delete motif). Portaled to <body> so it escapes the card's clip. Fully keyboard operable:
// focus moves into the menu on open, Arrow/Home/End rove between items, Escape or a click outside
// closes and returns focus to the trigger, Tab closes too. The armed-delete state is announced via an
// sr-only live region. When `blockDelete` is set, Delete is an inert, reason-giving item instead
// (deleting would orphan a nested pathway) — still focusable so a screen reader reads the reason.
/**
 * The shell every board popup menu shares: portal, placement under its anchor, roving focus on the
 * arrow keys, Escape and Tab to dismiss, and a pointer-down anywhere else to close.
 *
 * Extracted when the insert point needed a menu of its own. A second copy of this keyboard handling
 * is the kind of duplication that drifts — the card menu would gain a behaviour and the insert menu
 * would quietly not have it.
 */
function MenuShell({ open, onClose, anchorRef, label, children }: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  label: string;
  children: React.ReactNode;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const r = anchorRef.current?.getBoundingClientRect();
    // Right-aligned to the anchor, clamped so a menu opened at the left margin stays on screen.
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 176) });
    const items = () => [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const raf = requestAnimationFrame(() => items()[0]?.focus());
    const back = () => { onClose(); anchorRef.current?.focus(); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); back(); return; }
      const list = items(); if (!list.length) return;
      const i = list.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (list[(i + 1) % list.length] ?? list[0])!.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (list[(i - 1 + list.length) % list.length] ?? list[0])!.focus(); }
      else if (e.key === 'Home') { e.preventDefault(); list[0]!.focus(); }
      else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]!.focus(); }
    };
    const onDown = (e: PointerEvent) => {
      const tgt = e.target as Node;
      if (menuRef.current?.contains(tgt) || anchorRef.current?.contains(tgt)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div ref={menuRef} role='menu' aria-label={label} data-noedit style={{ top: pos.top, left: pos.left }}
      className={`fixed ${POPUP_LEVEL} w-44 overflow-hidden rounded-md border border-ws-border bg-ws-elevated py-1 shadow-lg`}>
      {children}
    </div>,
    document.body,
  );
}

const MENU_ITEM = 'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ws-text hover:bg-ws-hover focus:bg-ws-hover focus:outline-none';

function ActionMenu({ open, onClose, anchorRef, onCopy, copyLabel, onPastePathway, onDuplicate, onDelete, blockDelete }: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onCopy?: () => void;
  /** What Copy is copying. A pathway bar is a SET, so "Copy Task" would be a lie about the scope. */
  copyLabel?: string;
  /**
   * Hang the clipboard's pathway off THIS task. Absent whenever there is nothing to paste or the
   * task is already at the depth cap, so the item is never a dead row.
   *
   * This is the complete route, and the reason it exists alongside the insert pill: the pill only
   * appears where a task has a plain sequential successor, so the last task in a lane and any task
   * behind a redirect have no pill at all — and both are perfectly good places to hang a pathway.
   */
  onPastePathway?: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  blockDelete: boolean;
}) {
  const { t } = useTranslation();
  const [delArmed, setDelArmed] = useState(false);
  useEffect(() => { if (!open) setDelArmed(false); }, [open]);

  return (
    <MenuShell open={open} onClose={onClose} anchorRef={anchorRef} label={t('common.more')}>
      {/* Copy above Duplicate: same gesture at two scopes, and Duplicate is the shortcut for
          copy-then-paste-here. Same icon family for the same reason. */}
      {onCopy && (
        <button type='button' role='menuitem' tabIndex={-1} onClick={() => { onCopy(); onClose(); }} className={MENU_ITEM}>
          <Copy className='h-4 w-4 text-ws-muted' aria-hidden='true' />{copyLabel ?? t('experience.paste.copyTask')}
        </button>
      )}
      <button type='button' role='menuitem' tabIndex={-1} onClick={() => { onDuplicate(); onClose(); }} className={MENU_ITEM}>
        <CopyPlus className='h-4 w-4 text-ws-muted' aria-hidden='true' />{t('common.duplicate')}
      </button>
      {onPastePathway && (
        <button type='button' role='menuitem' tabIndex={-1} onClick={() => { onPastePathway(); onClose(); }} className={MENU_ITEM}>
          <ClipboardPaste className='h-4 w-4 text-ws-muted' aria-hidden='true' />{t('experience.paste.pastePathwayHere')}
        </button>
      )}
      <div className='my-1 border-t border-ws-border' role='separator' />
      {blockDelete ? (
        <div role='menuitem' aria-disabled='true' tabIndex={-1}
          aria-label={`${t('common.delete')}: ${t('experience.builder.deleteBlockedByPathways')}`}
          title={t('experience.builder.deleteBlockedByPathways')}
          className='flex items-center gap-2.5 px-3 py-2 text-sm text-ws-muted focus:outline-none'>
          <Trash2 className='h-4 w-4' aria-hidden='true' />{t('common.delete')}
        </div>
      ) : (
        <button type='button' role='menuitem' tabIndex={-1}
          onClick={() => { if (!delArmed) { setDelArmed(true); return; } onDelete(); onClose(); }}
          aria-label={delArmed ? t('common.deleteConfirm') : t('common.delete')}
          className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors focus:outline-none ${delArmed ? 'bg-ws-warning-bg font-semibold text-ws-warning-fg' : 'text-ws-warning-fg hover:bg-ws-warning-bg focus:bg-ws-warning-bg'}`}>
          <Trash2 className='h-4 w-4' aria-hidden='true' />{delArmed ? t('common.deleteConfirm') : t('common.delete')}
        </button>
      )}
      <span className='sr-only' role='status' aria-live='assertive'>{delArmed ? t('common.deleteConfirm') : ''}</span>
    </MenuShell>
  );
}

/**
 * The `+` between two tasks in a lane.
 *
 * A plain circle at rest — one target, one meaning, exactly as it always was. With a task on the
 * clipboard it MORPHS into a split pill on hover or focus, offering New Task and Paste as two
 * one-click halves. Standing the pill up permanently put a second glyph in every gap in the board,
 * which read as clutter for a choice that is only occasionally wanted; a menu hid both options
 * behind a disclosure and cost two interactions to reach either.
 *
 * The same three-input rule the block gaps use, for the same reason — a tap must not paste
 * something the teacher was only reaching for:
 *
 *   - mouse: hovering has already opened it, so the click that follows does what it looks like.
 *   - touch: nothing hovers, so the first tap only opens the pill and the second chooses.
 *   - keyboard: `detail === 0` is a key press, which acts outright; focus opens it first so the
 *     halves are visible while they are focusable.
 *
 * With an empty clipboard none of this applies: there is one half, and it inserts on one click.
 */
function InsertPoint({ size, coarse, x, y, onInsert, onPaste, onPastePathway, pasteLabel, hostLabel, open, onOpen, onClose }: {
  size: number;
  coarse: boolean;
  x: number;
  y: number;
  onInsert: () => void;
  onPaste?: () => void;
  /**
   * Hang the clipboard off the card ABOVE this point, as a new pathway.
   *
   * The third half is not a new referent: every half here already acts relative to `e.fromId`, the
   * task this insert point comes after. Half one puts a new task after it, half two puts the
   * clipboard after it in this lane, half three hangs the clipboard off it. Absent when the
   * clipboard holds nothing, or when the result would pass the depth cap.
   */
  onPastePathway?: () => void;
  pasteLabel?: string;
  /** The task above, for the branch half's label — which is what makes the two pastes distinguishable. */
  hostLabel?: string;
  /** Owned by the board, so opening one pill closes the last — touch fires no `mouseleave`. */
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canPaste = !!onPaste && !!pasteLabel;
  const canBranch = canPaste && !!onPastePathway;
  const showPill = canPaste && open;
  /** 2.4x for two halves, 3.4x for three — see the note below on why it is not a flat multiple. */
  const openWidth = Math.round(size * (canBranch ? 3.4 : 2.4));
  /** Whether it was already open when this press STARTED: a mouse clicking a pill it has been
   *  hovering, versus a touch meeting a circle for the first time. */
  const openAtPressStart = useRef(false);
  const half = 'flex items-center justify-center text-ws-muted transition-colors hover:text-ws-primary';
  const glyph = coarse ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <div
      data-insert-point=''
      onMouseEnter={canPaste ? onOpen : undefined}
      onMouseLeave={canPaste ? onClose : undefined}
      /*
       * Width AND height on the CONTAINER, with the halves flexing inside it.
       *
       * Sizing the button instead left the container to size itself to its content plus its own
       * 1px borders — `size + 2` wide against a `size` height, so the "circle" was a visibly
       * oblong pill, and the button's box was 2px taller than the space it sat in, which pushed
       * the glyph off centre. `box-sizing: border-box` only does the right thing here if both
       * dimensions are stated.
       */
      /*
       * Grows to the RIGHT, anchored on the collapsed circle's left edge — hence `left: x - size/2`
       * and no `-translate-x-1/2`, where a centred container would have grown both ways.
       *
       * Centred expansion moved the `+` out from under the pointer that opened it: 14px on a mouse,
       * 20px at `coarse` sizing — 60% of a 34px target — so the press that opened the pill landed
       * on a glyph that was no longer there. Left-growth is also the wrong direction twice over: the
       * spine sits at the board's left edge, so on phone portrait the pill expanded toward (and off)
       * the screen edge.
       *
       * Covering part of the card to the right is deliberate and costs nothing: the closed hit area
       * is one small circle, and anyone who has opened the pill is choosing an insert or a paste,
       * not reaching for the card underneath it.
       */
      className={`absolute z-10 flex -translate-y-1/2 overflow-hidden rounded-full border border-ws-border-strong bg-ws-surface shadow-sm transition-[width,opacity] duration-150 ${coarse || showPill ? '' : 'opacity-30 hover:opacity-100'}`}
      /* 2.4× rather than 2×: at exactly two halves the glyphs sit close enough to the `rounded-full`
         ends to read as cramped, since the curve is tightest right where a centred icon lands. The
         extra width buys each half a little air without making the pill a bar. */
      style={{ left: x - size / 2, top: y, width: showPill ? openWidth : size, height: size }}
    >
      <button
        type='button'
        onPointerDown={canPaste ? () => { openAtPressStart.current = open; onOpen(); } : undefined}
        onClick={(e) => {
          if (!canPaste || e.detail === 0 || openAtPressStart.current) onInsert();
        }}
        onFocus={canPaste ? onOpen : undefined}
        aria-label={t('experience.builder.insertHere')}
        title={t('experience.builder.insertHere')}
        className={`${half} h-full flex-1 hover:bg-ws-hover`}
      >
        <Plus className={glyph} />
      </button>
      {showPill && (
        <>
          <div aria-hidden='true' className='w-px self-stretch bg-ws-border-strong' />
          <button
            type='button'
            onClick={() => { onClose(); onPaste!(); }}
            aria-label={t('experience.paste.pasteTask', { title: pasteLabel })}
            title={t('experience.paste.pasteTask', { title: pasteLabel })}
            className={`${half} h-full flex-1 hover:bg-ws-primary/10`}
          >
            <ClipboardPaste className={glyph} />
          </button>
          {/* The third half differs from the second by DESTINATION, not by payload — the clipboard
              already says what is being pasted. So the mark is directional, read off the board's own
              geometry: lanes run down, branches run right. */}
          {canBranch && (
            <>
              <div aria-hidden='true' className='w-px self-stretch bg-ws-border-strong' />
              <button
                type='button'
                onClick={() => { onClose(); onPastePathway!(); }}
                aria-label={t('experience.paste.pastePathwayOff', { title: hostLabel ?? '' })}
                title={t('experience.paste.pastePathwayOff', { title: hostLabel ?? '' })}
                className={`${half} h-full flex-1 hover:bg-ws-primary/10`}
              >
                <CornerDownRight className={glyph} />
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

const SLIDE_EASE = 'cubic-bezier(0.22,1,0.36,1)';

/**
 * FLIP the SLIDE (pathway collapse/expand reflow) onto `transform` instead of `left`/`top`, so it runs on
 * the GPU compositor with no per-frame layout — important because authoring happens on phones/tablets.
 * When `enabled` and the element's target x/y change, it INVERTS (jumps back to the old spot via transform,
 * pre-paint in useLayoutEffect) then PLAYS (transitions the transform to 0). Returns the transform + whether
 * a transition should apply; the element clears it on transitionend. Untouched by drag (drag stays on its
 * own transform / left-top path — this only runs while `enabled`, which callers gate to the slide window).
 */
function useSlideFlip(x: number, y: number, enabled: boolean) {
  const prev = useRef({ x, y });
  const [flip, setFlip] = useState<{ dx: number; dy: number; play: boolean } | null>(null);
  useLayoutEffect(() => {
    const p = prev.current;
    prev.current = { x, y };
    if (!enabled || (p.x === x && p.y === y)) { setFlip((f) => (f ? null : f)); return undefined; }
    setFlip({ dx: p.x - x, dy: p.y - y, play: false }); // invert: sit at the old position, no transition
    const raf = requestAnimationFrame(() => setFlip((f) => (f && !f.play ? { ...f, play: true } : f))); // play
    return () => cancelAnimationFrame(raf);
  }, [x, y, enabled]);
  const transform = flip ? (flip.play ? 'translate(0px,0px)' : `translate(${flip.dx}px,${flip.dy}px)`) : undefined;
  const clear = () => setFlip((f) => (f?.play ? null : f));
  return { transform, playing: !!flip?.play, active: !!flip, clear };
}

function PathwayBar({ id, pathwayId, name, count, collapsed, x, y, w, readOnly, dragging, slideAnim, blockDelete, scoringActive, selectMode, selected, onToggleSelected, onRename, onToggleCollapse, onCopy, onDuplicate, onDelete, onMove }: { id: string; pathwayId: string; name: string; count: number; collapsed: boolean; x: number; y: number; w: number; readOnly: boolean; dragging: boolean; slideAnim?: boolean; blockDelete: boolean; scoringActive?: boolean; selectMode?: boolean; selected?: boolean; onToggleSelected?: () => void; onRename: (v: string) => void; onToggleCollapse: () => void; onCopy: () => void; onDuplicate: () => void; onDelete: () => void; onMove: (dir: -1 | 1) => void }) {
  const { t } = useTranslation();
  const [val, setVal] = useState(name);
  useEffect(() => setVal(name), [name]);
  // ⋯ menu (Duplicate / Delete the whole pathway) — the shared accessible ActionMenu, same as cards.
  const [menu, setMenu] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const closeMenu = () => setMenu(false);
  const openMenu = () => setMenu((m) => !m);
  // The whole pathway is draggable by its grip — dropping it on a host's hotspot re-attaches the
  // pathway to that task, or between pathways to reorder. dnd-kit id is namespaced `pw:` so the drag
  // handlers can tell a pathway move from a task move.
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `pw:${pathwayId}` });
  const flip = useSlideFlip(x, y, !!slideAnim && !dragging);
  return (
    <div ref={setNodeRef} data-card={collapsed ? id : undefined} data-noedit
      onTransitionEnd={(e) => { if (e.propertyName === 'transform') flip.clear(); }}
      {...(selectMode ? {
        onClick: onToggleSelected,
        // A real control while the mode is on: dnd-kit supplies role/tabIndex for the drag grip when
        // it is not, and a pathway has to be selectable from the keyboard like any task.
        role: 'button' as const,
        tabIndex: 0,
        'aria-pressed': !!selected,
        'aria-label': t('experience.select.togglePathway', { name }),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelected?.(); }
        },
      } : {})}
      /* In select mode the bar picks its WHOLE pathway — copying a host together with the pathway
         hanging off it is the motivating case, and clicking every member to get there is what would
         make the mode not worth entering. */
      className={`group absolute flex items-center gap-1 rounded-lg border bg-ws-primary-subtle/70 pl-0.5 pr-2 shadow-sm backdrop-blur-sm ${dragging ? 'opacity-40' : ''} ${
        selectMode && selected ? 'cursor-pointer border-ws-primary ring-2 ring-ws-primary'
        : selectMode ? 'cursor-pointer border-ws-text-link/35'
        : 'border-ws-text-link/35'
      }`}
      style={{ left: x, top: y, width: w, height: PATHWAY_BAR_H, transform: dragging ? (transform ? CSS.Translate.toString(transform) : undefined) : flip.transform, transition: flip.active ? (flip.playing ? `transform 260ms ${SLIDE_EASE}` : 'none') : undefined, zIndex: dragging ? 40 : 20 }}>
      {/* Grip = drag handle for MOVING the whole pathway. dnd-kit's spread supplies role/tabIndex; the
          arrow-key rail (up/down) reorders the pathway among its host's siblings without a pointer. */}
      {selectMode ? (
        // Inert in select mode: the bar itself is the control there, and a live grip (or the name
        // field) swallowed the click, leaving the whole pathway selectable only by accident.
        <div aria-hidden='true' className='pointer-events-none flex h-6 w-5 shrink-0 items-center justify-center rounded text-ws-text-link/60 opacity-30'>
          <GripVertical className='h-4 w-4' />
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- dnd-kit's spread supplies role="button"+tabIndex
        <div {...attributes} {...listeners} data-noedit aria-label={t('experience.builder.pathwayMove')} title={t('experience.builder.pathwayMove')}
          aria-keyshortcuts='ArrowUp ArrowDown'
          onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); onMove(-1); } else if (e.key === 'ArrowDown') { e.preventDefault(); onMove(1); } }}
          className='flex h-6 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-ws-text-link/60 transition-colors hover:text-ws-text-link active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-ws-primary'>
          <GripVertical className='h-4 w-4' />
        </div>
      )}
      {selectMode && !selected && (
        <div aria-hidden='true' className='pointer-events-none absolute inset-0 rounded-lg bg-ws-bg/60' />
      )}
      <Footprints className='h-4 w-4 shrink-0 text-ws-text-link' aria-hidden='true' />
      <input
        value={val}
        disabled={readOnly || selectMode}
        // `pointer-events-none` as well as disabled: a disabled input still swallows the press on
        // some engines, and the bar's click is how a whole pathway gets picked.
        style={selectMode ? { pointerEvents: 'none' } : undefined}
        // Long names truncate to an ellipsis; surface the full text on hover so it stays readable
        // without opening the field (only when it's actually long enough to be worth a tooltip).
        title={val.length > 18 ? val : undefined}
        onChange={(e) => setVal(e.target.value)}
        // Typing a long name scrolls the input to the caret at the end; on blur, rewind to the start so
        // the truncated display reads "Beginning of name…" rather than the tail "…end of the name".
        onBlur={(e) => { e.currentTarget.scrollLeft = 0; if (val !== name) onRename(val); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setVal(name); e.currentTarget.blur(); } }}
        placeholder={t('experience.builder.pathwayUnnamed')}
        aria-label={t('experience.builder.pathwayNameLabel')}
        className='min-w-0 flex-1 truncate rounded-md border-b-2 border-transparent bg-transparent px-1 py-0.5 -mx-1 text-sm font-medium text-ws-text transition-colors placeholder:font-normal placeholder:text-ws-text-link/70 hover:bg-ws-text-link/10 focus:border-ws-text-link focus:bg-ws-text-link/5 focus:outline-none'
      />
      {collapsed && (
        <span className='shrink-0 rounded-full bg-ws-primary px-2 py-0.5 text-[10px] font-bold leading-none text-white'>{t('experience.builder.pathwayTaskCountShort', { count })}</span>
      )}
      {!scoringActive && !selectMode && (
        <button ref={menuBtnRef} type='button' onClick={openMenu} disabled={readOnly}
          aria-label={t('common.more')} title={t('common.more')}
          className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ws-text-link/70 transition-colors hover:bg-ws-text-link/15 hover:text-ws-text-link disabled:opacity-40'>
          <MoreHorizontal className='h-4 w-4' />
        </button>
      )}
      <button type='button' onClick={onToggleCollapse} disabled={readOnly}
        aria-expanded={!collapsed}
        aria-label={collapsed ? t('experience.builder.pathwayExpand') : t('experience.builder.pathwayCollapse')}
        title={collapsed ? t('experience.builder.pathwayExpand') : t('experience.builder.pathwayCollapse')}
        className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ws-text-link transition-colors hover:bg-ws-text-link/15 disabled:opacity-40'>
        {collapsed ? <ChevronRight className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
      </button>
      <ActionMenu open={menu} onClose={closeMenu} anchorRef={menuBtnRef} onCopy={onCopy} copyLabel={t('experience.paste.copyPathway')} onDuplicate={onDuplicate} onDelete={onDelete} blockDelete={blockDelete} />
    </div>
  );
}

/**
 * New experience builder: a vertical spine (the Main flow) with redirect-columns (Branches), derived
 * entirely from the flow graph via `deriveGraph` — never the phantom level/order grid. The spine
 * reorders with the same block-style drag as the task's block list: an outside rail with up/down
 * arrows and a grip, a ghosted card while dragging, and neighbours reflowing to open a gap.
 * Horizontal (cross-lane) moves are deferred; branches are created from a card's ⋯ menu for now.
 */

const CW = 308;   // card width (desktop/tablet) — matched to the phone card (a phone column renders
                  // ~308px wide), which the product owner preferred over the narrower 264
/* The chip strip's own geometry, shared by the height maths above and the strip that draws it —
   two derivations of one layout is how a card comes to be the wrong size for its contents. */
const CHIP_GAP = 6;           // `gap-1.5` between chips; the last chip in a row needs none
const CHIP_PITCH = 26 + CHIP_GAP;
const CHIP_ROW_H = CHIP_PITCH;
/*
 * Everything between the card's edges and the strip's own content box, which has to be EXACT: a
 * guess that is too small computes a row that fits one more chip than it does, the strip wraps a
 * row the card was never grown for, and the last row is cut off at the border — which is what the
 * first version of this did. Left to right: the drag rail, the body's `pl-2.5`, the body's `pr-8`
 * (the gutter the ⋯ and the corner info button sit in) and the strip's own `pl-7`.
 */
const CHIP_INSET_FINE = 30 + 10 + 32 + 28;    // measured: a 308px card leaves the strip 208px
const CHIP_INSET_COARSE = 52 + 10 + 32 + 28;  // a touch rail is w-10 targets rather than w-5
/* A badge hangs 4px past its chip's bottom corner and a highlight ring 3px past that, so the last
   row needs more under it than the body's own `py-2`. */
const CHIP_STRIP_PAD = 8;
const MAX_CHIP_ROWS = 3;

/**
 * HOW MANY CHIPS A CARD DRAWS, AND OVER HOW MANY ROWS — one answer, read by both the height maths
 * and the strip, because the card being the wrong size for its own contents is precisely what two
 * derivations of this produce.
 *
 * The cap is three rows, except that a flagged block is NEVER the one dropped: the whole point of
 * the strip is to point at it. A task with more flagged blocks than three rows hold therefore grows
 * past the cap rather than hiding one.
 *
 * The "+N" counts as a slot. It is a chip-sized thing at the end of the list, and a plan that
 * ignored it sent it onto a row of its own below a full three — which is exactly the overflow the
 * rows were capped to prevent. So a capped strip shows one chip fewer and the count rides inline.
 */
export function chipPlan(total: number, flagged: number, perRow: number): { max: number; rows: number } {
  const cap = perRow * MAX_CHIP_ROWS;
  const max = flagged > cap ? flagged : (total <= cap ? total : cap - 1);
  const slots = max + (total > max ? 1 : 0);
  return { max, rows: Math.max(1, Math.ceil(slots / perRow)) };
}

const CH = 88;    // card height — fits a two-line (clamped) title plus the block-composition strip
const VG = 16;    // vertical gap between stacked cards
const CGAP = 92;  // horizontal gap between redirect columns (desktop) — wide enough for a redirect
                  // connector to show its arrowhead + an event label in the gap
const HEADER_H = 30; // column-header strip above the board
// Below this container width the board windows to one column (phone): each column grows to nearly
// fill the viewport (a sliver of the next peeks), so you scroll sideways to follow a branch.
const NARROW_MAX = 560;

/** Where a dragged card will land: a NEW branch off `parentId`, or a slot in a lane relative to
 * `ofId` (before/after it). Drives the live drag preview + the eventual drop. */
type DropIntent = { kind: 'branch'; parentId: string } | { kind: 'lane'; ofId: string; before: boolean };

function sameIntent(a: DropIntent | null, b: DropIntent | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'branch' && b.kind === 'branch') return a.parentId === b.parentId;
  if (a.kind === 'lane' && b.kind === 'lane') return a.ofId === b.ofId && a.before === b.before;
  return false;
}

/** deriveGraph + terminal detection + computeLayout for a task list. `expandedHeads` = pathway heads
 * whose pathway is OPEN — they get a header bar reserved ABOVE the head. `collapsedHeads` = heads
 * whose pathway is CLOSED — the head slot shrinks to just the bar (its members are hidden). */
function buildBoardLayout(boardTasks: Task[], cw: number, cardHeight: (id: string) => number, cgap: number, expandedHeads?: Set<string>, collapsedHeads?: Set<string>) {
  const graph = deriveGraph(boardTasks);
  const looseFrom = new Set(graph.edges.filter((e) => e.kind === 'loose').map((e) => e.fromId));
  const terminalIds = new Set<string>();
  for (const tk of boardTasks) {
    const c = deriveTaskEvents(tk).correct.action;
    if ((c.type === 'next' && !looseFrom.has(tk.id)) || c.type === 'stay' || c.type === 'end') terminalIds.add(tk.id);
  }
  const layout = computeLayout(graph.spine, graph.detoursOf, { cw, ch: cardHeight(''), vg: VG, cgap }, {
    // A collapsed pathway hides its End cap + New Task, so it must NOT reserve the 74px tail for them
    // — that reserved-but-empty band was the dead space stacking collapsed pathways far apart.
    reserveBottom: (id) => terminalIds.has(id) && !collapsedHeads?.has(id), tailGap: 74,
    reserveTop: (id) => !!expandedHeads?.has(id), headGap: PATHWAY_HEAD_GAP,
    heightOf: (id) => (collapsedHeads?.has(id) ? PATHWAY_BAR_H : cardHeight(id)),
    // Collapsed pathways are short bars — stack them tightly so a host with several of them doesn't
    // balloon the branch column and stretch the spine. A full card keeps the normal gap.
    gapAfter: (id) => (collapsedHeads?.has(id) ? PATHWAY_STACK_GAP : VG),
  });
  return { graph, terminalIds, layout };
}

const PATHWAY_BAR_H = 34;  // the pathway bar's rendered height (both states)
const PATHWAY_STACK_GAP = 8; // vertical gap between consecutive COLLAPSED pathway bars (tighter than VG)
const REORDER_EDGE = 0.28; // fraction of a card's height (top & bottom) that reorders; the middle branches
const PATHWAY_HEAD_GAP = PATHWAY_BAR_H + 8; // space reserved above an EXPANDED head so the bar sits over it
                                    // with a small gap; equals the collapsed head's slot, so the bar's
                                    // top lands at the same y in both states — the chevron never jumps

interface HeadInfo { name: string; count: number; pathwayId: string; collapsed: boolean }

/** Remap a flow's goto targets through `map` (a member → its collapsed pathway's head). */
function remapFlowTargets(flow: NonNullable<Task['flow']>, map: Map<string, string>): NonNullable<Task['flow']> {
  const fix = <A extends { type: string; targetTaskId?: string | null }>(a: A): A =>
    (a.type === 'goto' && a.targetTaskId && map.has(a.targetTaskId) ? { ...a, targetTaskId: map.get(a.targetTaskId)! } : a);
  return {
    ...flow,
    allCorrectAction: fix(flow.allCorrectAction),
    defaultAction: fix(flowDefaultAction(flow)),
    noAnswerAction: fix(flow.noAnswerAction),
    eventRules: flowEventRules(flow).map((r) => ({ ...r, action: fix(r.action) })),
  };
}

/** Collapse view: which pathways exist (headInfo), and — for COLLAPSED ones — hide the non-head
 * members and map them to the head so boundary wires re-point to the header. The head (first member
 * in array order) becomes the collapsed header card / expanded header anchor. */
function collapseView(taskList: Task[]): { displayTasks: Task[]; headOf: Map<string, string>; headInfo: Map<string, HeadInfo> } {
  const byPathway = new Map<string, Task[]>();
  for (const t of taskList) if (t.pathwayId) { const a = byPathway.get(t.pathwayId); if (a) a.push(t); else byPathway.set(t.pathwayId, [t]); }
  const headOf = new Map<string, string>();
  const headInfo = new Map<string, HeadInfo>();
  const hidden = new Set<string>();
  for (const [pathwayId, members] of byPathway) {
    const head = members[0]!;
    const collapsed = !!head.pathwayCollapsed;
    headInfo.set(head.id, { name: head.pathwayName ?? '', count: members.length, pathwayId, collapsed });
    if (collapsed) for (const m of members) if (m.id !== head.id) { hidden.add(m.id); headOf.set(m.id, head.id); }
  }
  const displayTasks = taskList
    .filter((t) => !hidden.has(t.id))
    .map((t) => (t.flow && headOf.size ? { ...t, flow: remapFlowTargets(t.flow, headOf) } : t));
  return { displayTasks, headOf, headInfo };
}

/** A rendered edge, plus (when a collapse remapped its endpoints) the ORIGINAL endpoints — so the
 * event-category lookup can still find the real source event / real target task that the wire carries.
 * Without this, a wire aggregated onto a collapsed head loses its category and defaults to "correct". */
type BoardEdge = GraphEdge & { origFromId?: string; origToId?: string | null };

/** Re-point edge endpoints through `map` (collapsed members → head); drop now-internal self-loops.
 * Remembers each edge's pre-remap endpoints so its category survives the collapse. */
function remapEndpoints(edges: GraphEdge[], map: Map<string, string>): BoardEdge[] {
  if (!map.size) return edges;
  const out: BoardEdge[] = [];
  const seen = new Set<string>();
  for (const e of edges) {
    const from = map.get(e.fromId) ?? e.fromId;
    const to = e.toId ? (map.get(e.toId) ?? e.toId) : e.toId;
    if (from === to) continue; // wholly inside a collapsed pathway
    const key = `${from}>${to ?? 'Ø'}:${e.kind}`;
    if (seen.has(key)) continue; seen.add(key);
    out.push({ ...e, fromId: from, toId: to, origFromId: e.fromId, origToId: e.toId });
  }
  return out;
}

/** Board view WITH pathway collapse applied — the layout/graph/edges the builder renders. `dragging`
 * opens the per-host "new pathway" hotspot band beneath each host's pathways. */
function buildView(taskList: Task[], cw: number, cardHeight: (id: string) => number, cgap: number) {
  const cv = collapseView(taskList);
  const expandedHeads = new Set([...cv.headInfo].filter(([, i]) => !i.collapsed).map(([id]) => id));
  const collapsedHeads = new Set([...cv.headInfo].filter(([, i]) => i.collapsed).map(([id]) => id));
  const board = buildBoardLayout(cv.displayTasks, cw, cardHeight, cgap, expandedHeads, collapsedHeads);
  // Edges from the FULL task list, endpoints remapped, so a collapsed pathway's boundary wires
  // (incoming + all members' returns/jumps) aggregate onto the header; internals vanish.
  const edges = remapEndpoints(deriveGraph(taskList).edges, cv.headOf);
  // A collapsed head that gained an aggregated OUTGOING edge (a return/jump from a hidden member) is
  // not a dead-end — drop it from the End-cap set the display layout computed from the now-hidden
  // internals. (A truly returnless collapsed pathway keeps its End cap.)
  const hasOut = new Set(edges.filter((e) => e.toId).map((e) => e.fromId));
  const terminalIds = new Set([...board.terminalIds].filter((id) => !hasOut.has(id)));
  return {
    graph: { ...board.graph, edges, looseThreads: edges.filter((e) => e.kind === 'loose-thread') },
    terminalIds,
    layout: board.layout,
    headInfo: cv.headInfo,
  };
}

/** The tasks array as it would be AFTER a drag drop resolves to `intent` — the source of the preview
 * layout and the committed order. Re-parents + repositions the dragged card; leaves the rest intact. */
const noPathway = { pathwayId: undefined, pathwayName: undefined, pathwayCollapsed: undefined };
function applyDrop(all: Task[], dragId: string, intent: DropIntent): Task[] {
  const dragged = all.find((t) => t.id === dragId);
  if (!dragged) return all;
  const rest = all.filter((t) => t.id !== dragId);
  let moved: Task; let idx: number;
  let joinedPathwayId: string | undefined; // set when the drop joins an existing pathway → expand it
  if (intent.kind === 'branch') {
    // Dropped to CREATE a new branch off a card → a new remediation pathway → its own pathway.
    moved = { ...dragged, pathwayOf: intent.parentId, pathwayId: `pw_${crypto.randomUUID()}`, pathwayName: '', pathwayCollapsed: false };
    // Land it at the END of the host's existing branch list (array order = lane order), not the top:
    // insert after the parent AND after every task already in the parent's branch subtree.
    const byIdRest = new Map(rest.map((t) => [t.id, t] as const));
    const descendsFromParent = (t: Task): boolean => {
      const seen = new Set<string>();
      let curId = t.pathwayOf;
      while (curId && !seen.has(curId)) {
        if (curId === intent.parentId) return true;
        seen.add(curId);
        curId = byIdRest.get(curId)?.pathwayOf ?? undefined;
      }
      return false;
    };
    let last = rest.findIndex((t) => t.id === intent.parentId);
    for (let k = 0; k < rest.length; k++) if (descendsFromParent(rest[k]!)) last = Math.max(last, k);
    idx = last < 0 ? rest.length : last + 1;
  } else {
    const over = all.find((t) => t.id === intent.ofId);
    // Pathway coherence: a spine task is NEVER pathwayd. Landing in a branch lane joins the
    // neighbour's pathway; if the neighbour is loose, mint a new one (so a task moved into Branches
    // always belongs to a pathway — and a sequence dropped adjacently coalesces, since each later
    // drop then inherits the one before it).
    // Joining an existing pathway EXPANDS it (collapsed:false) so the drop is visible where it landed.
    const env = !over?.pathwayOf
      ? noPathway
      : over.pathwayId
        ? { pathwayId: over.pathwayId, pathwayName: over.pathwayName, pathwayCollapsed: false }
        : { pathwayId: `pw_${crypto.randomUUID()}`, pathwayName: '', pathwayCollapsed: false };
    moved = { ...dragged, pathwayOf: over?.pathwayOf, ...env };
    joinedPathwayId = over?.pathwayId ?? undefined;
    const oi = rest.findIndex((t) => t.id === intent.ofId);
    idx = oi < 0 ? rest.length : intent.before ? oi : oi + 1;
  }
  const out = [...rest];
  out.splice(idx, 0, moved);
  // Expand the whole joined pathway (all members), not just the moved task — so it opens on drop.
  return joinedPathwayId ? out.map((t) => (t.pathwayId === joinedPathwayId ? { ...t, pathwayCollapsed: false } : t)) : out;
}

/** Force one pathway to render COLLAPSED (drag-time only) — used while DRAGGING a whole pathway, so
 * you move a single compact bar instead of a tall stack of member cards. View override only. */
function collapsePathway(tasks: Task[], pathwayId: string | null): Task[] {
  if (!pathwayId) return tasks;
  let changed = false;
  const out = tasks.map((t) => {
    if (t.pathwayId === pathwayId && !t.pathwayCollapsed) { changed = true; return { ...t, pathwayCollapsed: true }; }
    return t;
  });
  return changed ? out : tasks;
}

/** The tasks array after MOVING a whole pathway (all its member tasks, contiguous, internal order
 * preserved) to a new host + position — the source of both the pathway-drag preview and its commit.
 * `intent.branch` = attach to a host (append after its existing pathways); `intent.lane` = reorder
 * relative to another pathway. Every member's `pathwayOf` is re-pointed to the new host. */
function applyDropPathway(all: Task[], pathwayId: string, intent: DropIntent): Task[] {
  const members = all.filter((t) => t.pathwayId === pathwayId);
  if (!members.length) return all;
  const memberIds = new Set(members.map((m) => m.id));
  const rest = all.filter((t) => !memberIds.has(t.id));
  let host: string | undefined; let idx: number;
  if (intent.kind === 'branch') {
    host = intent.parentId;
    // Append after the host's whole existing branch subtree (same placement as a new pathway).
    const byIdRest = new Map(rest.map((t) => [t.id, t] as const));
    const descends = (t: Task): boolean => {
      const seen = new Set<string>();
      let c = t.pathwayOf;
      while (c && !seen.has(c)) { if (c === host) return true; seen.add(c); c = byIdRest.get(c)?.pathwayOf ?? undefined; }
      return false;
    };
    let last = rest.findIndex((t) => t.id === host);
    for (let k = 0; k < rest.length; k++) if (descends(rest[k]!)) last = Math.max(last, k);
    idx = last < 0 ? rest.length : last + 1;
  } else {
    const over = rest.find((t) => t.id === intent.ofId);
    host = over?.pathwayOf ?? undefined;
    // Reorder relative to the target pathway as a WHOLE — insert before its FIRST member or after its
    // LAST, never between its tasks (which would split the target pathway's sequence). `ofId` is the
    // target's head, so its raw index+1 lands inside it — bracket the whole pathway instead.
    if (over?.pathwayId) {
      let first = -1, last = -1;
      rest.forEach((t, i) => { if (t.pathwayId === over.pathwayId) { if (first < 0) first = i; last = i; } });
      idx = intent.before ? first : last + 1;
    } else {
      const oi = rest.findIndex((t) => t.id === intent.ofId);
      idx = oi < 0 ? rest.length : intent.before ? oi : oi + 1;
    }
  }
  if (!host) return all; // a pathway must hang off a host task; ignore an invalid target
  const moved = members.map((m) => ({ ...m, pathwayOf: host }));
  const out = [...rest];
  out.splice(idx, 0, ...moved);
  return out;
}

export default function SpineBuilder({
  tasks,
  readOnly,
  scrollRef: externalScrollRef,
  focusTaskId,
  hoverTaskId,
  pulseTaskIds,
  onFocusHandled,
  onEditTask,
  onCreateTask,
  onDeleteTask,
  onDuplicateTask,
  onReorder,
  onMoveWithinLane,
  onOutdent,
  onRenamePathway,
  onTogglePathwayCollapsed,
  onCopyPathway,
  onPastePathwayOnto,
  selectMode,
  selectedIds,
  onToggleSelected,
  onShiftSelect,
  selectedPathwayIds,
  onTogglePathwaySelected,
  pathwayClipboardDepth,
  onDuplicatePathway,
  onDeletePathway,
  onWireEvent,
  onDisconnectEvents,
  onWireToNewBranch,
  onInsertAfter,
  onCopyTask,
  onPasteAfter,
  pasteLabel,
  onMakeBranchOf,
  scoringActive,
  errorsActive,
  problemsByTask,
  highlighted,
  onOpenBlock,
  onSetTaskScoring,
}: {
  tasks: Task[];
  readOnly: boolean;
  /** The board's scroll container, shared with the overview map (viewport box + scroll-to). */
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>;
  /** When set (e.g. from a map node tap), scroll that card into view and pulse it once. */
  focusTaskId?: string | null;
  /** A block hovered in the overview map — ring the matching card (no scroll). */
  hoverTaskId?: string | null;
  /** Set alongside `focusTaskId` only when the caller MEANT "look here". A delete moves focus to the
   *  survivor so focus is never stranded on <body>, which is not a reason to flash the survivor.
   *
   *  A LIST, because a step is not always one task: undoing "Apply to Main Flow" moves every spine
   *  task back at once, and flashing only the one the viewport landed on understates it. The
   *  viewport still goes to `focusTaskId` alone — it can only go to one place. */
  pulseTaskIds?: string[] | null;
  onFocusHandled?: () => void;
  onEditTask: (task: Task) => void;
  onCreateTask: () => void;
  onDeleteTask: (taskId: string) => void;
  onDuplicateTask: (taskId: string) => void;
  /** Commit a full reordered/re-parented tasks array (the drag drop's result). */
  onReorder: (tasks: Task[]) => void;
  /** Reorder a card up/down within its own lane (spine or branch) — the keyboard rail. */
  onMoveWithinLane: (taskId: string, dir: -1 | 1) => void;
  /** Outdent (`<`): pop a branch card out to its parent's lane (spine at column 1). */
  onOutdent: (taskId: string) => void;
  /** Pathways (remediation pathways): rename, collapse/expand, duplicate as a sibling, delete whole. */
  onRenamePathway: (pathwayId: string, name: string) => void;
  onTogglePathwayCollapsed: (pathwayId: string) => void;
  onCopyPathway: (pathwayId: string) => void;
  /** Hang the clipboard's pathway off a task. Absent when the clipboard holds nothing pasteable. */
  onPastePathwayOnto?: (hostTaskId: string) => void;
  /** Nesting inside the clipboard set, so the board can tell which hosts it would still fit under. */
  pathwayClipboardDepth?: number;
  /** Select mode — scoping, not editing. Suppresses the same affordances the scoring aspect does. */
  selectMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelected?: (taskId: string) => void;
  onShiftSelect?: (taskId: string) => void;
  /** Pathways whose every member is selected — the bar shows lit, and clicking it clears them. */
  selectedPathwayIds?: Set<string>;
  onTogglePathwaySelected?: (pathwayId: string) => void;
  onDuplicatePathway: (pathwayId: string) => void;
  onDeletePathway: (pathwayId: string) => void;
  /** Point a source task's event at a target task id, or 'end'. */
  onWireEvent: (sourceTaskId: string, eventId: string, target: string) => void;
  /** Disconnect a wire — revert all its branch events to retry-in-place (folded into one update). */
  onDisconnectEvents: (sourceTaskId: string, eventIds: string[]) => void;
  /** Wire an event to a brand-new contextualized branch task off the source (opens its editor). */
  onWireToNewBranch: (sourceTaskId: string, eventId: string) => void;
  /** Insert a new task right after this one in its lane, auto-rewiring the through-line. */
  onInsertAfter: (afterTaskId: string) => void;
  /** Put this task on the clipboard. */
  onCopyTask: (taskId: string) => void;
  /** Paste the clipboard's tasks after this one. Absent when the clipboard holds no tasks, which is
   *  what keeps every insert point a plain one-click insert until there is a choice to offer. */
  onPasteAfter?: (afterTaskId: string) => void;
  /** What the clipboard holds, for the menu item's label. */
  pasteLabel?: string;
  /** Make an existing task a branch of another (keyboard `>` indent → set pathwayOf). */
  onMakeBranchOf: (taskId: string, parentId: string) => void;
  /** Scoring aspect active → cards show scoring controls instead of the block strip; structural
   * affordances (port, insert, editor-open) are quieted. */
  scoringActive?: boolean;
  /**
   * ERRORS aspect active — a peer of Scoring, and the board treats the two alike wherever the
   * question is "is an aspect on": both stand the wiring down so the board reads for one thing at
   * a time. They differ in what the card BODY becomes, and in what a click means. A click here
   * still OPENS the task, because the panel's rows own the go-to verb; in Scoring it selects.
   */
  errorsActive?: boolean;
  /** Per task id, for the errors aspect. Absent for a task with nothing wrong. */
  problemsByTask?: Map<string, TaskProblemSummary>;
  /** The problem the panel's pointer is over: its task scrolls into view and its block lights. */
  highlighted?: { taskId: string; blockId?: string };
  /** Open one block of a task directly, from its chip. */
  onOpenBlock?: (taskId: string, blockId: string) => void;
  onSetTaskScoring?: (taskId: string, patch: { scoringMode: ScoringMode; points: number; penalty: number }) => void;
}) {
  /* ONE QUESTION, asked everywhere the wiring stands down: is an aspect on. Scoring and Errors
     both hide the ports, the edges and the per-card menu, because both exist to let the board be
     read for one thing at a time. Only what the card BODY becomes, and what a click means,
     differs — so those two read the specific flag instead. */
  const anyAspect = scoringActive || errorsActive;
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);
  // Index of the edge whose wire is being hovered (via its hit-stroke or its ✕) — drives the
  // on-wire disconnect affordance for branch connectors.
  const [hoveredEdge, setHoveredEdge] = useState<number | null>(null);
  // The wire whose delete control is ARMED (first click) — a second click confirms, matching the
  // app-wide click-to-arm / click-to-delete motif. Cleared whenever the hovered wire changes.
  const [armedEdge, setArmedEdge] = useState<number | null>(null);
  // A disconnect ARMED from a card's port Connected list (first click). While set, the matching board
  // wire + its target card highlight in warning colour, so it's unmistakable which connection a second
  // click will drop. `{ sourceId, eventId }`; eventId 'correct' targets the correct return/goto.
  const [pendingDisconnect, setPendingDisconnect] = useState<{ sourceId: string; eventId: string } | null>(null);
  // A connected event merely HOVERED/focused in a port list (not yet armed). Emphasises its wire and
  // fades every other flow line, so you can see exactly which connection a row refers to before acting.
  const [portFocusEvent, setPortFocusEvent] = useState<{ sourceId: string; eventId: string } | null>(null);

  // Wiring: arm an open event from a card's port, then connect it to a target task (or End). Two
  // input modes, one plumbing (onWireEvent): TAP the event then tap a target, or DRAG from the
  // event row onto a target. A drag past a small threshold arms as it goes and draws a live wire.
  const [arming, setArming] = useState<{ sourceId: string; event: TaskEvent } | null>(null);
  /** Which insert point is showing its paste half, by the task it inserts after. One owner, so
   *  opening any pill closes the last — without it a touch screen leaves a trail of open pills. */
  const [openInsert, setOpenInsert] = useState<string | null>(null);

  // A tap anywhere that is not an insert point closes the open pill. Touch has no pointer to move
  // away, so without this the only way to dismiss one is to open another.
  useEffect(() => {
    if (openInsert === null) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement | null)?.closest?.('[data-insert-point]')) setOpenInsert(null);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [openInsert]);
  const [wireDrag, setWireDrag] = useState<{ ax: number; ay: number; x: number; y: number } | null>(null);
  const wireDragRef = useRef<{ sourceId: string; event: TaskEvent; startX: number; startY: number; ax: number; ay: number; moved: boolean } | null>(null);
  useEffect(() => {
    if (!arming) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setArming(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [arming]);
  // Arming is a transient gesture — drop it only if the source task itself disappears (deleted or
  // forked to a new id), so a background autosave reconcile mid-gesture doesn't cancel it.
  useEffect(() => {
    if (arming && !tasks.some((tk) => tk.id === arming.sourceId)) setArming(null);
  }, [tasks, arming]);

  // Pointer-drag a wire from a port event row onto a target card. Kept off @dnd-kit (which owns the
  // spine-reorder DndContext) — a plain window-pointer drag, so the two never contend. A tap (no
  // travel) falls through to the row's click, which arms tap-to-wire instead.
  function beginWireDrag(sourceId: string, event: TaskEvent, startX: number, startY: number) {
    // Suppress the native text selection that a window-pointer drag otherwise smears across the page.
    document.body.style.userSelect = 'none';
    window.getSelection?.()?.removeAllRanges();
    const rect = document.querySelector(`[data-card="${sourceId}"]`)?.getBoundingClientRect();
    wireDragRef.current = {
      sourceId, event, startX, startY,
      ax: rect ? rect.right : startX,
      ay: rect ? rect.top + rect.height / 2 : startY,
      moved: false,
    };
    const onMove = (e: PointerEvent) => {
      const d = wireDragRef.current;
      if (!d) return;
      if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 6) return;
      if (!d.moved) { d.moved = true; setArming({ sourceId: d.sourceId, event: d.event }); } // arm as the drag starts
      setWireDrag({ ax: d.ax, ay: d.ay, x: e.clientX, y: e.clientY });
    };
    const onUp = (e: PointerEvent) => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const d = wireDragRef.current;
      wireDragRef.current = null;
      setWireDrag(null);
      if (!d) return;
      // The port row unmounted when the gesture began (panel closed on pointerdown), so this
      // pointerup synthesizes a click on whatever is now under it — swallow it once in the capture
      // phase so it can't open a card's editor. Self-clears on fire, or after a beat. (Keyboard
      // activation has no pointer sequence and arms via the row's own onClick instead.)
      const swallow = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); window.removeEventListener('click', swallow, true); };
      window.addEventListener('click', swallow, true);
      setTimeout(() => window.removeEventListener('click', swallow, true), 350);
      if (!d.moved) { setArming({ sourceId: d.sourceId, event: d.event }); return; } // a tap → arm tap-to-wire
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const targetId = el?.closest('[data-card]')?.getAttribute('data-card') ?? null;
      const onBoard = !!el?.closest('[data-board]'); // dropped inside the board, not off-canvas
      if (targetId && targetId !== d.sourceId) { onWireEvent(d.sourceId, d.event.id, targetId); setArming(null); }
      else if (!targetId && onBoard) { onWireToNewBranch(d.sourceId, d.event.id); setArming(null); } // drop on empty → create + contextualize
      // released over the source or off-canvas → stay armed so the banner + tap-to-wire continues
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  // Measure the scroll container so column width can grow to fill a narrow (phone) viewport.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [viewportW, setViewportW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewportW(el.clientWidth));
    ro.observe(el);
    setViewportW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Coarse pointer (touch) → grow the card and its reorder rail so the up/grip/down controls are
  // finger-sized (~44px); fine pointer (mouse) keeps the compact card. Not tied to viewport width —
  // a narrow desktop window is still a mouse.
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const on = () => setCoarse(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  // Respect the OS "reduce motion" setting — gates the card-position glide, the jump-to pulse, and
  // smooth scrolling, so a drag/reorder/jump doesn't animate for motion-sensitive users.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduceMotion(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const narrow = viewportW > 0 && viewportW < NARROW_MAX;
  const cw = narrow ? Math.max(240, Math.round((viewportW - 40) * 0.88)) : CW;
  // Portrait phone: one column per screen. Set the pitch to a full viewport so each redirect column
  // is a swipe away (snap-aligned below) and the arrows have room to arc — at the old 24px gap the
  // columns crowded together and the redirects had no space, distorting into near-vertical kinks.
  // Landscape (wider than NARROW_MAX) uses the desktop layout, which already shows ~two columns.
  const cgap = narrow ? Math.max(48, viewportW - cw) : CGAP;
  // Portrait phone: pad the scroll container by half the leftover so the FIRST column sits centred in
  // the viewport (not jammed against the left edge) and, with snap-align centre, every column below
  // centres as you swipe. The padding also lets the last column reach a centred snap position.
  const sidePad = narrow ? Math.max(20, Math.round((viewportW - cw) / 2)) : 20;
  // taller on touch to fit three ~40px rail targets. In the scoring aspect the single mode-cycler +
  // points/per-mistake row is only a little taller than the block strip: on desktop add a small +24 so it
  // clears the slot; on touch the 128 base already absorbs it, so NO premium — otherwise touch scoring
  // cards balloon a notch past the normal touch card. (Touch stays at 128, closer to desktop's 112.)
  /**
   * THE ERRORS ASPECT GROWS THE CARD TO FIT ITS CHIPS, because the chips are the aspect.
   *
   * `BlockStrip` keeps to one row by deduplicating types and capping at six; a per-BLOCK strip
   * cannot, and a task of a dozen blocks wrapped to a second row that the fixed height cut in
   * half — the owner's "anything with more than one line of block icons gets cut in half".
   *
   * EACH CARD TO ITS OWN CONTENT. Sizing every card to the worst task on the board made the whole
   * board puffy to fit one busy task; `computeLayout` already takes a `heightOf` (a collapsed
   * pathway is a short bar), so growth is per card and the rest of the board stays where it was.
   * Capped at three rows by `chipPlan`, which also decides how many chips the strip draws — one
   * answer for both, or the card is the wrong size for what is in it.
   */
  const chipsPerRow = Math.max(1, Math.floor(
    (cw - (coarse ? CHIP_INSET_COARSE : CHIP_INSET_FINE) + CHIP_GAP) / CHIP_PITCH));
  const baseCh = (coarse ? 128 : CH) + (scoringActive && !coarse ? 24 : 0);

  const boardTasks = tasks;
  const byId = useMemo(() => new Map(tasks.map((tk) => [tk.id, tk])), [tasks]);
  /** This task's chip plan. Called with a collapsed pathway's id (and, for the layout's own
   *  default, with no id at all), so it answers "one row" for anything it does not know. */
  const chipPlanOf = useCallback((id: string) => {
    const task = byId.get(id);
    if (!errorsActive || !task) return { max: 0, rows: 1 };
    const blocks = task.blocks ?? [];
    const p = problemsByTask?.get(id);
    const flagged = p ? blocks.filter((b) => p.blockErrors.has(b.id) || p.blockWarnings.has(b.id)).length : 0;
    return chipPlan(blocks.length, flagged, chipsPerRow);
  }, [errorsActive, byId, problemsByTask, chipsPerRow]);
  const chOf = useCallback((id: string) => baseCh
    + (chipPlanOf(id).rows - 1) * CHIP_ROW_H
    + (errorsActive ? CHIP_STRIP_PAD : 0), [baseCh, chipPlanOf, errorsActive]);
  // Tasks that HOST pathways/branches (some task hangs off them). Deleting one would orphan its
  // branch tasks onto the spine, so its Delete is blocked until those pathways are moved or deleted.
  const hostIds = useMemo(() => new Set(tasks.map((tk) => tk.pathwayOf).filter((p): p is string => !!p)), [tasks]);
  // Pathways with a MEMBER that itself hosts a (nested) pathway — deleting the whole pathway would
  // orphan that nested one, so the pathway's Delete is blocked too.
  const pathwaysWithNested = useMemo(() => {
    const s = new Set<string>();
    for (const tk of tasks) if (tk.pathwayId && hostIds.has(tk.id)) s.add(tk.pathwayId);
    return s;
  }, [tasks, hostIds]);
  // Ids a goto can validly point at. An event wired to a target NOT in here (e.g. a task that was
  // later deleted) is a dangling link — the port treats it as OPEN again so it's re-linkable, not
  // stuck "unavailable" forever.
  const validTargets = useMemo(() => new Set(tasks.map((tk) => tk.id)), [tasks]);
  // Each LANE as an ordered id list (spine = '__spine', a branch = its pathwayOf). Powers the rail's
  // within-lane up/down + indent (branch off the card above) for both spine and branch cards.
  const lanes = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const tk of boardTasks) {
      const key = tk.pathwayOf ?? '__spine';
      const arr = m.get(key); if (arr) arr.push(tk.id); else m.set(key, [tk.id]);
    }
    return m;
  }, [boardTasks]);
  // Each task's events, so a drawn edge can name itself (which event routes down it).
  const eventsByTask = useMemo(() => new Map(boardTasks.map((tk) => [tk.id, deriveTaskEvents(tk)])), [boardTasks]);

  // Drag state: the card being dragged + where it will land. The board renders a live PREVIEW of the
  // post-drop layout while dragging, so cards displace to show the insertion (and a new-branch drop
  // pushes an existing branch down). The dragged card itself floats at the pointer, over the gap.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIntent, setDropIntent] = useState<DropIntent | null>(null);
  // Hit-testing works in CONTENT space (the same coordinates `layout.placed` uses), read from the live
  // cursor. The board never reflows DURING a drag (no hover-expand, no hotspot bands) — the only thing
  // that moves is the drop-preview of OTHER cards on a REORDER — so a drag stays visually solid.
  // `groupRef` is the layout origin (top-left of the placed cards); `pointerRef` is the live cursor in
  // viewport space, updated in the capture phase so it's fresh before dnd-kit's own move handler runs.
  const groupRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const dragging = dragId != null;
  useEffect(() => {
    if (!dragging) return;
    // Track the live cursor for hit-testing. Listen to pointer AND touch AND mouse: TouchSensor
    // preventDefaults touchmove (to stop the scroll), which can suppress the synthesised
    // pointermove/mousemove on touch — so touchmove is the reliable source there, pointer/mouse on
    // desktop. Capture phase so pointerRef is fresh before dnd-kit's own move handler runs.
    const setFrom = (x: number, y: number) => { pointerRef.current = { x, y }; };
    const onPointer = (e: PointerEvent) => setFrom(e.clientX, e.clientY);
    const onMouse = (e: MouseEvent) => setFrom(e.clientX, e.clientY);
    const onTouch = (e: TouchEvent) => { const p = e.touches[0] ?? e.changedTouches[0]; if (p) setFrom(p.clientX, p.clientY); };
    window.addEventListener('pointermove', onPointer, true);
    window.addEventListener('mousemove', onMouse, true);
    window.addEventListener('touchmove', onTouch, true);
    return () => {
      window.removeEventListener('pointermove', onPointer, true);
      window.removeEventListener('mousemove', onMouse, true);
      window.removeEventListener('touchmove', onTouch, true);
    };
  }, [dragging]);
  // A `pw:`-namespaced drag id means the WHOLE pathway is being moved (grabbed by its bar), not a task.
  const pathwayDragId = dragId && dragId.startsWith('pw:') ? dragId.slice(3) : null;
  // Dragging a pathway collapses it to a single bar so you move one compact chip, not a tall stack.
  const real = useMemo(() => buildView(collapsePathway(boardTasks, pathwayDragId), cw, chOf, cgap), [boardTasks, cw, chOf, cgap, pathwayDragId]);
  const previewTasks = useMemo(
    () => {
      if (!dragId || !dropIntent) return null;
      // A BRANCH drop shows only a ring on the target card — no board reflow — so dropping on a card
      // feels solid and nothing jumps. Only a REORDER previews the insertion (the gap opening up).
      if (dropIntent.kind === 'branch') return null;
      return pathwayDragId ? applyDropPathway(tasks, pathwayDragId, dropIntent) : applyDrop(tasks, dragId, dropIntent);
    },
    [dragId, pathwayDragId, dropIntent, tasks],
  );
  const preview = useMemo(() => (previewTasks ? buildView(collapsePathway(previewTasks, pathwayDragId), cw, chOf, cgap) : null), [previewTasks, cw, chOf, cgap, pathwayDragId]);
  const active = preview ?? real;
  const graph = active.graph;
  const headInfo = active.headInfo; // pathway headId -> { name, count, pathwayId, collapsed }
  const terminalIds = active.terminalIds;
  const layout = active.layout;

  // Expand/collapse animation. The position transition (the "slide") must be ARMED before the layout
  // actually changes, or the browser has nothing to interpolate from — that's why setting it in an effect
  // AFTER the toggle looked instant. So `animatedToggleCollapse` turns the slide window on FIRST, then
  // commits the collapse on the next frame. Collapsing also fades the members OUT before they unmount;
  // expanding fades them IN (see `enterAnim`). All gated on reduced-motion.
  const collapseSig = useMemo(() => [...headInfo].filter(([, i]) => i.collapsed).map(([id]) => id).sort().join('|'), [headInfo]);
  const [layoutAnim, setLayoutAnim] = useState(false);
  const [collapsingPathways, setCollapsingPathways] = useState<Set<string>>(() => new Set());
  const prevCollapseSig = useRef(collapseSig);
  useEffect(() => { prevCollapseSig.current = collapseSig; }, [collapseSig]); // advance ref for enterAnim
  // True ONLY on the render where the collapse set just changed (effect above advances the ref right
  // after). Member cards that mount on an expand read this at mount, start hidden, then fade in.
  const enterAnim = !reduceMotion && prevCollapseSig.current !== collapseSig;
  const slideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armSlide = () => {
    setLayoutAnim(true);
    if (slideTimer.current) clearTimeout(slideTimer.current);
    slideTimer.current = setTimeout(() => setLayoutAnim(false), 460);
  };
  const animatedToggleCollapse = (pathwayId: string, collapsed: boolean) => {
    if (reduceMotion) { onTogglePathwayCollapsed(pathwayId); return; }
    if (collapsed) {
      // Expanding: arm the slide window + commit together — the FLIP inverts pre-paint (useLayoutEffect),
      // so members mount + fade in while everything below glides down. No frame-defer needed.
      armSlide();
      onTogglePathwayCollapsed(pathwayId);
    } else {
      // Collapsing: fade the members OUT first (still mounted), then commit + slide neighbours up.
      setCollapsingPathways((s) => new Set(s).add(pathwayId));
      setTimeout(() => {
        setCollapsingPathways((s) => { const n = new Set(s); n.delete(pathwayId); return n; });
        armSlide();
        onTogglePathwayCollapsed(pathwayId);
      }, 180);
    }
  };
  useEffect(() => () => { if (slideTimer.current) clearTimeout(slideTimer.current); }, []);
  // Tasks that SOMETHING routes to (any loose-next or goto edge targets them). A branch task with no
  // incoming edge has "no entrance". This is LOCAL, not transitive: we flag the task that's actually
  // orphaned, not everything downstream of it — a task IS entered if a predecessor flows into it, even
  // if that predecessor is itself unreachable (the predecessor gets its own flag).
  const incomingTargets = useMemo(() => {
    const s = new Set<string>();
    for (const e of graph.edges) if (e.toId) s.add(e.toId);
    return s;
  }, [graph]);
  const colCount = useMemo(() => {
    let max = 0;
    for (const p of layout.placed.values()) max = Math.max(max, p.col);
    return max + 1;
  }, [layout]);

  /*
   * BRANCHING IS FLOW, drawn on the board instead of listed in a panel.
   *
   * A branch IS a redirect: the Branches columns are derived from where this task's events send a
   * student, so offering them to a teacher who has Flow turned off would be offering the whole
   * feature through a side door — and the side door is the easier one to find, because it is a
   * drag rather than a panel.
   *
   * ONE CAP DOES IT ALL. Every way to make a branch already asks the same question — "would the
   * new column still be within the depth limit" — so lowering the limit to zero closes the drop on
   * a card body, the pathway drop, the paste-pathway menu item and the keyboard `>` indent
   * together. Four affordances, one substitution, and no fifth one that forgot: anything that
   * learns to branch later has to pass this same test to work at all.
   *
   * `MAX_BRANCH_COL` itself stays where existing cards are measured against it (`tooDeep`), so a
   * board that already HAS branches keeps drawing and warning about them exactly as before.
   */
  const branchesAllowed = useCapability('flow') || colCount > 1;
  const branchCap = branchesAllowed ? MAX_BRANCH_COL : 0;

  // Screen-reader live region. Structural edits (reorder / indent / outdent / branch / delete /
  // duplicate) are otherwise silent — the keyboard rail changes the layout with no spoken feedback —
  // so every such action calls announce() with a short outcome sentence. The nonce toggles a trailing
  // NBSP so two identical messages in a row still re-fire (a live region ignores an unchanged string).
  const [liveMsg, setLiveMsg] = useState('');
  const liveNonce = useRef(false);
  // eslint-disable-next-line no-irregular-whitespace -- the alternate branch appends a non-breaking space on purpose
  const announce = (msg: string) => { liveNonce.current = !liveNonce.current; setLiveMsg(liveNonce.current ? msg : `${msg} `); };
  const titleOf = (id: string) => byId.get(id)?.title ?? '';
  /**
   * Whether the clipboard's pathway would still fit hanging off a host in column `col`.
   *
   * The head lands one column past the host and the set brings its own nesting, so this is the same
   * sum `fitsBranchDepth` makes — expressed here against the layout's `col`, which is the number
   * the board already has for every placed card.
   */
  const pastePathwayFits = (col: number) => col + 1 + (pathwayClipboardDepth ?? 0) <= branchCap;

  /**
   * BRING THE HOVERED PROBLEM'S TASK INTO VIEW — scroll only.
   *
   * Deliberately NOT `focusTaskId`, which also moves keyboard focus and pulses the card. Both
   * would be wrong for a hover: focus belongs to the panel row the pointer is on, and a pulse is
   * for "look here, something happened" rather than for a preview the reader can end by moving
   * the mouse.
   *
   * CENTRED, though, not `nearest`. Nearest is the smallest scroll that satisfies the word "in
   * view", which lands the card hard against the top or bottom edge with its neighbours — and the
   * relationship to what is around it is half of what the preview is for. The browser already
   * clamps at the ends of the scroller, so a first or last card simply sits as close to the middle
   * as the board allows.
   */
  useEffect(() => {
    const taskId = highlighted?.taskId;
    if (!taskId) return;
    scrollRef.current?.querySelector<HTMLElement>(`[data-card="${taskId}"]`)
      ?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center', inline: 'center' });
  }, [highlighted?.taskId, reduceMotion]);

  // Jump-to-task from the overview map: scroll the card into view and pulse it once.
  const [pulse, setPulse] = useState<{ ids: Set<string>; n: number } | null>(null);
  // The fade timer lives in a ref, NOT the effect's cleanup: `onFocusHandled()` clears `focusTaskId` in
  // the same tick, so a cleanup keyed on `focusTaskId` would cancel the timer immediately and the pulse
  // ring would stay forever. The ref timer survives that and fades the ring on its own schedule.
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!focusTaskId) return;
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-card="${focusTaskId}"]`);
    el?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center', inline: 'center' });
    // Move real keyboard focus to the card's drag/reorder grip so focus is never left on <body> after
    // a create / insert / delete (the triggering button unmounts). The grip is the card's arrow-key
    // rail, so a keyboard user lands ready to reorder. Falls back to the card element if no grip.
    const grip = el?.querySelector<HTMLElement>('[aria-keyshortcuts]') ?? el;
    grip?.focus({ preventScroll: true });
    // Only when the caller MEANT "look here". A delete moves focus to the survivor so focus is
    // never stranded on <body>, and that is not a reason to flash the survivor — see `pulseTaskIds`.
    if (pulseTaskIds?.includes(focusTaskId)) {
      setPulse((prev) => ({ ids: new Set(pulseTaskIds), n: (prev?.n ?? 0) + 1 }));
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulse(null), 700);
    }
    onFocusHandled?.();
  }, [focusTaskId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (pulseTimer.current) clearTimeout(pulseTimer.current); }, []);

  // Half the node's rendered height — a collapsed pathway is a short bar, not a full card, so its
  // wires must anchor at the BAR's centre, otherwise they'd land ~10px below it in empty space.
  const halfH = (id: string) => (headInfo.get(id)?.collapsed ? PATHWAY_BAR_H / 2 : chOf(id) / 2);
  const center = (id: string) => { const p = layout.placed.get(id); return p ? { x: p.x + cw / 2, y: p.y + halfH(id) } : null; };
  const isAdjacent = (e: GraphEdge) => {
    if (!e.toId) return false;
    const a = layout.placed.get(e.fromId); const b = layout.placed.get(e.toId);
    if (!a || !b || a.col !== b.col || b.y <= a.y) return false;
    // Use each node's RENDERED height (a collapsed pathway is a short bar, not a full card). With the
    // full `ch`, several tightly-stacked collapsed bars all read as "adjacent", so a wire between two
    // NON-consecutive bars drew as a straight vertical line running behind the bars in between. Only a
    // genuinely next-down node (no full node fitting in the gap) is adjacent → drawn straight.
    return b.y - (a.y + halfH(e.fromId) * 2) < halfH(e.toId) * 2;
  };

  const spineNum = useMemo(() => { const m = new Map<string, number>(); graph.spine.forEach((id, i) => m.set(id, i + 1)); return m; }, [graph.spine]);

  // A task's through-line goes nowhere → it terminates its pathway (End cap). See terminalIds above.
  const isTerminal = (id: string): boolean => terminalIds.has(id);

  // The branch events (NOT the correct through-line) whose goto lands on this edge's target — the
  // ones a disconnect on this wire reverts to retry-in-place. Empty for the spine next hop or a
  // correct return (correct re-targets, never dangles — deferred). Where several branch events
  // share one target, the edge de-dupes to a single wire, so disconnecting it frees all of them.
  function edgeDisconnectEvents(e: BoardEdge): TaskEvent[] {
    if (e.kind !== 'locked') return [];
    // Use the ORIGINAL endpoints when the wire was remapped by a collapse — the real source event
    // targets the real (pre-collapse) task, not the head it now visually lands on.
    const fromId = e.origFromId ?? e.fromId;
    const toId = e.origToId ?? e.toId;
    if (!toId) return [];
    const src = eventsByTask.get(fromId);
    return src?.branches.filter((b) => b.wired && b.targetTaskId === toId) ?? [];
  }
  // The event CATEGORY a drawn edge carries — colours the connector + picks its terminator. Read
  // straight from the edge's PROVENANCE (deriveGraph tags every edge with the source event's
  // category), so a wire is coloured/labelled by the event that actually made it — never re-derived
  // by target-matching (which broke for a source whose event isn't currently surfaced, defaulting a
  // wrong/blank goto to the correct through-line's teal "All Correct").
  function edgeCategory(e: BoardEdge): EdgeCategory {
    return e.cat ?? 'route';
  }
  // Does this edge carry a given port connection ({ sourceId, eventId })? Matches on the ORIGINAL
  // (pre-collapse) source, so a wire aggregated onto a collapsed head still lights up. `correct` picks
  // the correct return/goto; a branch eventId picks the wire its goto lands on.
  function edgeMatchesConn(e: BoardEdge, conn: { sourceId: string; eventId: string } | null): boolean {
    if (!conn) return false;
    if ((e.origFromId ?? e.fromId) !== conn.sourceId) return false;
    if (conn.eventId === 'correct') return e.kind === 'locked' && edgeCategory(e) === 'route';
    return edgeDisconnectEvents(e).some((ev) => ev.id === conn.eventId);
  }
  // The connection currently emphasised from a port: the armed disconnect wins over a mere hover.
  const focusConn = pendingDisconnect ?? portFocusEvent;
  // Only fade the other wires when the focused connection actually HAS a wire to emphasise — an End
  // connection draws no line, so fading everything to spotlight nothing would just be confusing.
  const focusHasWire = !!focusConn && graph.edges.some((e) => edgeMatchesConn(e, focusConn));
  // Like edgeCategory, but a Special-rule wire gets the ★ terminator (its category is neutral, yet it
  // should read as the port's star, not the incomplete ring).
  function edgeTerm(e: BoardEdge): EdgeTerm {
    return e.special ? 'special' : edgeCategory(e);
  }
  // The hover-name for a wire — from provenance, so it matches the event that made it.
  function edgeLabel(e: BoardEdge): string {
    if (e.special) return e.label ?? '';
    if (e.cat === 'fallback') return t('task.flow.defaultRung.title');
    if (e.cat === 'gate') return t('task.flow.anyIncomplete.title');
    if (e.cat === 'sensor') return t('task.flow.noSensorData.title');
    return t('task.flow.allCorrect.title');
  }
  // Apex of a drawn edge (arc peak for an in-column redirect, else the chord midpoint) in board
  // coords — where the hover-name and the disconnect ✕ sit. Mirrors the path math below.
  function edgeApex(a: { x: number; y: number }, b: { x: number; y: number }, adjacent: boolean) {
    const sameCol = Math.abs(a.x - b.x) < 1;
    const bow = Math.min(64, 32 + Math.abs(b.y - a.y) * 0.12);
    return { x: sameCol && !adjacent ? a.x + cw / 2 + bow : (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  // ── Connector stacking ────────────────────────────────────────────────────────────────────────
  // Several curved redirects can touch the SAME card edge — a task that both sends a branch OUT and
  // receives its RETURN, or a spine task that several branches fan back into. Left unmanaged, their
  // originators/terminators pile onto one point and read as a tangle (the product owner's "neutral
  // overlapping a return, crossing under a correct return, under a correct out"). Group every
  // redirect ENDPOINT by the card edge it attaches to (card id + side), then fan each group into
  // evenly spaced vertical slots. Order within a group: an "out" (forward hop, to a deeper column)
  // rides ABOVE a "return" (backward hop) — so a one-task round-trip always reads out-above-return;
  // ties break by category then the other endpoint's id, so the layout is stable frame to frame.
  const edgeSlots = useMemo(() => {
    const MAX_SLOT = 13;
    // Keep the whole fan inside the card's own edge (small margin top+bottom). Per card, since a
    // card grown for a second chip row has that much more edge to spread across.
    const bandOf = (id: string) => chOf(id) - 16;
    type EP = { idx: number; end: 'orig' | 'term'; forward: boolean; cat: string; other: string };
    const groups = new Map<string, EP[]>();
    const add = (key: string, ep: EP) => { const g = groups.get(key); if (g) g.push(ep); else groups.set(key, [ep]); };
    graph.edges.forEach((e, idx) => {
      if (e.kind !== 'locked' || !e.toId) return; // only curved redirects stack; through-lines are straight verticals
      if (isAdjacent(e)) return;                   // an adjacent redirect is drawn straight too
      const a = center(e.fromId); const b = center(e.toId);
      if (!a || !b) return;
      const forward = b.x >= a.x;
      const sameCol = Math.abs(a.x - b.x) < 1;
      const cat = edgeCategory(e);
      const origSide = sameCol || forward ? 'R' : 'L'; // where the wire leaves the source card
      const termSide = sameCol ? 'R' : forward ? 'L' : 'R'; // where it meets the target card
      add(`${e.fromId}|${origSide}`, { idx, end: 'orig', forward, cat, other: e.toId });
      add(`${e.toId}|${termSide}`, { idx, end: 'term', forward, cat, other: e.fromId });
    });
    const catRank: Record<string, number> = { route: 0, fallback: 1, gate: 2, sensor: 3 };
    const offsets = new Map<number, { orig: number; term: number }>();
    const ensure = (idx: number) => { let o = offsets.get(idx); if (!o) { o = { orig: 0, term: 0 }; offsets.set(idx, o); } return o; };
    for (const [key, g] of groups) {
      if (g.length < 2) continue; // a lone endpoint stays centred on its card edge
      g.sort((p, q) =>
        (p.forward === q.forward ? 0 : p.forward ? -1 : 1) ||   // out (forward) above return (backward)
        ((catRank[p.cat] ?? 3) - (catRank[q.cat] ?? 3)) ||
        (p.other < q.other ? -1 : p.other > q.other ? 1 : 0));
      // Full spacing until the fan would overflow the card edge, then compress to fit — so a crowded
      // fan-in (many branches returning to one spine task) packs tighter rather than spilling onto
      // the neighbouring cards.
      const slot = Math.min(MAX_SLOT, bandOf(key.slice(0, key.lastIndexOf('|'))) / (g.length - 1));
      const mid = (g.length - 1) / 2;
      g.forEach((ep, k) => {
        const o = ensure(ep.idx);
        if (ep.end === 'orig') o.orig = (k - mid) * slot; else o.term = (k - mid) * slot;
      });
    }
    return offsets;
  }, [graph.edges, layout, cw, chOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drag & drop: reorder within a lane, or move across lanes, with a live displacement preview ──
  // Mouse + Touch sensors rather than a single PointerSensor: PointerSensor loses touch drags inside a
  // scrollable container (the phone/portrait board scroll-snaps horizontally) — the browser hands the
  // gesture to the scroller and dnd-kit never gets pointerup, so the card stays ghosted. TouchSensor
  // uses touch events with a short press-delay: a quick swipe scrolls the board, a brief press-hold
  // starts a drag, and touchend reliably ends it. Mouse keeps the instant 6px-travel activation.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  // Descendants of the thing being dragged — never a valid drop parent (would form a pathwayOf cycle).
  // For a task drag: the task's branch subtree. For a WHOLE-pathway drag: the pathway's own member
  // tasks AND their deeper subtrees — so a pathway can never be re-attached to a task inside itself
  // (which would make it reference a member of itself and silently vanish). Computed from the raw
  // tasks (their pathwayOf edges), which is robust to the drag-time collapse of the real view.
  const dragDescendants = useMemo(() => {
    const s = new Set<string>();
    if (!dragId) return s;
    const childrenOf = new Map<string, string[]>();
    for (const tk of tasks) if (tk.pathwayOf) { const a = childrenOf.get(tk.pathwayOf) ?? []; a.push(tk.id); childrenOf.set(tk.pathwayOf, a); }
    const seed = pathwayDragId ? tasks.filter((tk) => tk.pathwayId === pathwayDragId).map((tk) => tk.id) : (childrenOf.get(dragId) ?? []);
    const stack = [...seed];
    while (stack.length) { const id = stack.pop()!; if (s.has(id)) continue; s.add(id); stack.push(...(childrenOf.get(id) ?? [])); }
    return s;
  }, [dragId, pathwayDragId, tasks]);
  // Resolve the drop intent from the cursor against the STATIC real layout. The model is simple and
  // reflow-free: cursor over a card's body → BRANCH off it; cursor in the gap between cards → REORDER
  // (lane-insert before/after). No hover-expand, no hotspot bands — the board never moves under the drag.

  // The live cursor in CONTENT space (same frame as `layout.placed`), read straight off `groupRef`'s
  // current rect so it stays correct through any reflow or scroll. Hit-testing follows the CURSOR, not
  // the dragged card's body: you point at the card you want, rather than aiming an offset ghost (the
  // grip sits at the card's left edge, so its body trails well to the right of the cursor). Null until
  // we have a pointer + group.
  const pointerContent = (): { x: number; y: number } | null => {
    const p = pointerRef.current, g = groupRef.current;
    if (!p || !g) return null;
    const r = g.getBoundingClientRect();
    return { x: p.x - r.left, y: p.y - r.top };
  };

  // The card/bar the cursor is targeting: the one it sits INSIDE if any (so pointing at a card always
  // picks that card), else the nearest by centre (for the gaps between cards / empty space). Never the
  // dragged thing itself, its own subtree (would cycle), nor a pathway's own bar while it's being moved.
  const targetCard = (px: number, py: number): Placed | null => {
    let inside: Placed | null = null, best: Placed | null = null, bestD = Infinity;
    for (const pp of real.layout.placed.values()) {
      if (pp.id === dragId || dragDescendants.has(pp.id)) continue;
      if (pathwayDragId && headInfo.get(pp.id)?.pathwayId === pathwayDragId) continue;
      const h = headInfo.get(pp.id)?.collapsed ? PATHWAY_BAR_H : chOf(pp.id);
      if (px >= pp.x && px <= pp.x + cw && py >= pp.y && py <= pp.y + h) inside = pp;
      const d = Math.hypot(px - (pp.x + cw / 2), py - (pp.y + halfH(pp.id)));
      if (d < bestD) { bestD = d; best = pp; }
    }
    return inside ?? best;
  };

  function intentFromPointer(): DropIntent | null {
    if (!dragId) return null;
    const p = pointerContent();
    if (!p) return null;
    const best = targetCard(p.x, p.y);
    if (!best) return null;
    const collapsed = !!headInfo.get(best.id)?.collapsed;
    const bh = collapsed ? PATHWAY_BAR_H : chOf(best.id);
    // Cursor over the card's BODY (its middle band) → branch off it. The top/bottom slivers and the gaps
    // between cards fall through to a lane-insert. A collapsed pathway bar is NOT a branch target (it
    // hides its tasks — open it first to branch off one). The depth cap still applies.
    const inBody = !collapsed && p.y >= best.y + bh * REORDER_EDGE && p.y <= best.y + bh * (1 - REORDER_EDGE);
    if (inBody && best.col + 1 <= branchCap) return { kind: 'branch', parentId: best.id };
    // Reorder: insert before/after the target card in its lane. A dragged PATHWAY can only reorder
    // against another pathway (it needs a host); in the gap by a bare spine task it has nothing to
    // reorder against, so it attaches as a branch off that task instead of silently snapping back.
    const before = p.y < best.y + halfH(best.id);
    if (pathwayDragId && !byId.get(best.id)?.pathwayOf) {
      return best.col + 1 <= branchCap ? { kind: 'branch', parentId: best.id } : null;
    }
    return { kind: 'lane', ofId: best.id, before };
  }
  function endDrag(commit: boolean) {
    if (commit && dragId && dropIntent) {
      // Announce the outcome for screen-reader users (the drag itself is silent).
      const movedTitle = pathwayDragId ? (tasks.find((tk) => tk.pathwayId === pathwayDragId)?.pathwayName || '') : titleOf(dragId);
      if (dropIntent.kind === 'branch') announce(t('experience.builder.a11y.branchedOff', { title: movedTitle, parent: titleOf(dropIntent.parentId) }));
      else announce(t('experience.builder.a11y.moved', { title: movedTitle }));
      onReorder(pathwayDragId ? applyDropPathway(tasks, pathwayDragId, dropIntent) : applyDrop(tasks, dragId, dropIntent));
    }
    setDragId(null);
    setDropIntent(null);
  }

  // Keyboard equivalent of dragging a whole pathway: the pathway grip's arrow rail reorders it among
  // its host's sibling pathways (up = before the previous sibling, down = after the next). Re-hosting
  // to a different task stays a pointer gesture. Keeps focus on the grip (same DOM node, keyed by id).
  function movePathway(pathwayId: string, dir: -1 | 1) {
    const members = tasks.filter((tk) => tk.pathwayId === pathwayId);
    const host = members[0]?.pathwayOf;
    if (!host) return;
    const siblings: string[] = [];
    for (const tk of tasks) if (tk.pathwayOf === host && tk.pathwayId && !siblings.includes(tk.pathwayId)) siblings.push(tk.pathwayId);
    const targetPw = siblings[siblings.indexOf(pathwayId) + dir];
    const targetHead = targetPw ? tasks.find((tk) => tk.pathwayId === targetPw)?.id : undefined;
    if (!targetHead) return;
    announce(t('experience.builder.a11y.moved', { title: members[0]?.pathwayName || '' }));
    onReorder(applyDropPathway(tasks, pathwayId, { kind: 'lane', ofId: targetHead, before: dir < 0 }));
  }

  // Lane bands (behind the cards): Main flow = column 0; Branches = every deeper column.
  const mainBand = { left: -8, width: cw + 16 };
  // Branches is a standing design element (drop target for pathways), so on desktop it's ALWAYS shown
  // — even with no branches yet — by reserving a second column's width. On a phone (narrow) the board
  // windows to one column, so it only appears once a branch exists.
  // Reserved only where branching is on offer — an empty Branches column is a standing
  // invitation, and inviting a teacher to the one thing the gate withholds is worse than
  // saying nothing. A board that HAS branches keeps its band regardless.
  const showBranchCol = colCount > 1 || (!narrow && branchesAllowed);
  // The drop PREVIEW (active layout) already includes a branch's new column, so its width covers it.
  const boardWidth = showBranchCol ? Math.max(layout.width, cw + cgap + cw + 20) : layout.width;
  const branchBand = showBranchCol
    ? { left: cw + cgap / 2, width: boardWidth - (cw + cgap / 2) }
    : null;

  return (
    <DndContext sensors={sensors} onDragStart={(e) => { const ae = e.activatorEvent as Partial<PointerEvent> & Partial<TouchEvent>; const p = ae?.touches?.[0] ?? ae?.changedTouches?.[0] ?? (ae as Partial<PointerEvent>); if (typeof p?.clientX === 'number' && typeof p?.clientY === 'number') pointerRef.current = { x: p.clientX, y: p.clientY }; setDragId(String(e.active.id)); }} onDragMove={() => { const next = intentFromPointer(); setDropIntent((prev) => (sameIntent(prev, next) ? prev : next)); }} onDragEnd={() => endDrag(true)} onDragCancel={() => endDrag(false)}>
    <div
      ref={(el) => { scrollRef.current = el; if (externalScrollRef) externalScrollRef.current = el; }}
      data-board
      className='h-full w-full overflow-auto p-5'
      style={narrow ? { scrollSnapType: 'x proximity', paddingLeft: sidePad, paddingRight: sidePad } : undefined}
    >
      {/* Screen-reader announcements for structural edits (see announce()). */}
      <div className='sr-only' role='status' aria-live='polite'>{liveMsg}</div>
      <div className='relative mx-auto' style={{ width: boardWidth, height: HEADER_H + layout.height }}>
        {/* Column lane bands + headers — structure only (Main flow / Branches); a branch's purpose
            is a per-branch label, added later, since one Branches column stacks branches of
            different tasks and purposes. */}
        <div className='pointer-events-none absolute rounded-xl bg-black/[0.025] dark:bg-white/[0.03]' style={{ left: mainBand.left, top: 0, width: mainBand.width, height: HEADER_H + layout.height }} />
        <div className='pointer-events-none absolute px-1 text-xs font-semibold uppercase tracking-wide text-ws-muted' style={{ left: mainBand.left + 8, top: 8 }}>{t('experience.builder.mainFlow')}</div>
        {branchBand && (
          <>
            <div className='pointer-events-none absolute rounded-xl bg-ws-text-link/[0.06]' style={{ left: branchBand.left, top: 0, width: branchBand.width, height: HEADER_H + layout.height }} />
            <div className='pointer-events-none absolute px-1 text-xs font-semibold uppercase tracking-wide text-ws-text-link/80' style={{ left: branchBand.left + 8, top: 8 }}>{t('experience.builder.branches')}</div>
          </>
        )}

        <div ref={groupRef} className='absolute inset-x-0' style={{ top: HEADER_H, height: layout.height }}>
          <svg aria-hidden='true' className='pointer-events-none absolute inset-0 overflow-visible' width={layout.width} height={layout.height}>
            <defs>
              {/* Terminator + colour carry the EVENT CATEGORY the connector routes, echoing the port
                  dots: correct = teal arrowhead (the mastery ADVANCE — a forward arrow, not a check);
                  the branches are glyphs in a same-colour ring on a surface chip — incorrect = ✗,
                  incomplete = dashed ring, special = ★. Sized in user units (stroke-independent). */}
              {/* The arrowhead is anchored at its BASE (refX≈0), not its tip, so the stroke ends where
                  the arrowhead begins; the path endpoint is pulled back so the tip lands ON the edge. */}
              <marker id='sb-route' markerUnits='userSpaceOnUse' markerWidth='9' markerHeight='9' refX='0.5' refY='4.5' orient='auto'>
                <path d='M0 0 L7 4.5 L0 9 z' fill='var(--color-ws-primary)' />
              </marker>
              {/* The same arrowhead, hollow: Default is a route like the through-line, just the one
                  nothing else claimed. It was a red ✕, which accused the student of being wrong
                  about a rung that only means "nothing above matched". */}
              <marker id='sb-fallback' markerUnits='userSpaceOnUse' markerWidth='9' markerHeight='9' refX='0.5' refY='4.5' orient='auto'>
                <path d='M0.8 1.4 L6.2 4.5 L0.8 7.6 z' fill='none' stroke='var(--color-ws-muted)' strokeWidth={1.3} strokeLinejoin='round' />
              </marker>
              <marker id='sb-gate' markerUnits='userSpaceOnUse' markerWidth='18' markerHeight='18' refX='9' refY='9'>
                <circle cx='9' cy='9' r='7.6' fill='var(--color-ws-surface)' />
                <circle cx='9' cy='9' r='5.6' fill='none' stroke='var(--color-ws-muted)' strokeWidth={1.7} strokeDasharray='2.4 2' strokeLinecap='round' />
              </marker>
              {/* A BARRED ring, not merely a differently coloured one: a dead sensor and a blank
                  answer are different facts, and colour alone is not a distinction to rest that on. */}
              <marker id='sb-sensor' markerUnits='userSpaceOnUse' markerWidth='18' markerHeight='18' refX='9' refY='9'>
                <circle cx='9' cy='9' r='7.6' fill='var(--color-ws-surface)' stroke='var(--color-ws-warning-fg)' strokeWidth={1.6} />
                <path d='M5.8 12.2 L12.2 5.8' stroke='var(--color-ws-warning-fg)' strokeWidth={1.9} strokeLinecap='round' />
              </marker>
              <marker id='sb-special' markerUnits='userSpaceOnUse' markerWidth='18' markerHeight='18' refX='9' refY='9'>
                <circle cx='9' cy='9' r='7.6' fill='var(--color-ws-surface)' stroke='var(--color-ws-text-link)' strokeWidth={1.6} />
                <path d='M9 4.7 L10 7.6 L13.1 7.65 L10.65 9.55 L11.5 12.5 L9 10.75 L6.5 12.5 L7.35 9.55 L4.9 7.65 L8 7.6 Z' fill='var(--color-ws-text-link)' />
              </marker>
            </defs>
            {/* Ghosted attachment threads — a faint dashed link from a host task to each PATHWAY that
                hangs off it, landing on the pathway's BAR (not its head card). This is STRUCTURAL
                "belongs-to", NOT a flow wire — anchoring it on the bar keeps it visually separate from
                real flow connections, which land on task cards. One thread per pathway (so a host with
                three pathways shows three), plus one per un-enveloped branch lane (legacy). Drawn
                behind the flow wires. */}
            {(() => {
              const threads: { hostId: string; headId: string; barY: number }[] = [];
              // Every pathway → its bar. The bar sits above the head when expanded, or in the head's
              // short slot when collapsed; either way land the thread at the bar's vertical centre.
              for (const [headId, info] of headInfo) {
                const hostId = byId.get(headId)?.pathwayOf;
                const p = layout.placed.get(headId);
                if (!hostId || !p) continue;
                const barTop = info.collapsed ? p.y : p.y - PATHWAY_HEAD_GAP;
                threads.push({ hostId, headId, barY: barTop + PATHWAY_BAR_H / 2 });
              }
              // Un-enveloped branch lanes keep a thread to their first task's card centre (as before).
              for (const [hostId, kids] of Object.entries(graph.detoursOf)) {
                const firstLoose = kids.find((k) => !byId.get(k)?.pathwayId);
                const p = firstLoose ? layout.placed.get(firstLoose) : null;
                if (firstLoose && p) threads.push({ hostId, headId: firstLoose, barY: p.y + chOf(firstLoose) / 2 });
              }
              return threads.map(({ hostId, headId, barY }) => {
                const a = center(hostId); const p = layout.placed.get(headId);
                if (!a || !p) return null;
                const ax = a.x + cw / 2, bx = p.x, mx = (ax + bx) / 2;
                const lit = hovered === hostId || hovered === headId;
                return (
                  <path key={`host-${hostId}-${headId}`} d={`M${ax} ${a.y} C${mx} ${a.y}, ${mx} ${barY}, ${bx} ${barY}`}
                    stroke='var(--color-ws-muted)' strokeWidth={1.5} strokeDasharray='2 4' fill='none' opacity={focusHasWire ? 0.06 : lit ? 0.6 : 0.28} />
                );
              });
            })()}
            {graph.edges.map((e, i) => {
              const a = center(e.fromId); const b = e.toId ? center(e.toId) : null;
              if (!a) return null;
              const lit = hovered === e.fromId || (e.toId && hovered === e.toId);
              if (e.kind === 'loose-thread') {
                return <path key={i} d={`M${a.x + cw / 2} ${a.y} L${a.x + cw / 2 + 44} ${a.y}`} stroke='var(--color-ws-warning-fg)' strokeWidth={2} fill='none' strokeDasharray='5 4' opacity={focusHasWire ? 0.08 : lit ? 1 : 0.5} />;
              }
              if (!b) return null;
              const adjacent = isAdjacent(e);
              // A loose edge is the sequential THROUGH-LINE (correct/next) — always within one lane,
              // so it renders as a straight vertical segment no matter how far the branch stretches
              // the lane apart. Consistent visual language for "advance": straight + solid, never
              // curved or dotted. Only a redirect (locked goto) curves.
              const through = e.kind === 'loose';
              const straight = through || adjacent;
              const disc = edgeDisconnectEvents(e); // non-empty → this wire can be disconnected
              const wireHot = hoveredEdge === i;
              // Colour + terminator by what the connector carries (Special breaks out to its star).
              const term = edgeTerm(e);
              const stroke = CAT_STROKE[term];
              const marker = CAT_MARKER[term];
              // A re-targeted correct (a locked goto whose category is correct — i.e. NOT the sequential
              // through-line) is a deletable link: deleting it reverts correct to `next`.
              const isCorrectGoto = e.kind === 'locked' && term === 'route';
              // The through-line sits quiet by default but brightens when its card is hovered, so a
              // correct arrow highlights along with the rest of the card's wires.
              const opacity = straight ? (lit ? 1 : 0.55) : (lit || wireHot ? 1 : 0.45);
              // An in-column redirect (goto whose target sits in the SAME column) would, as a plain
              // center-to-center curve, collapse onto the through-line and be invisible/unclickable.
              // Bow it out past the cards' shared right edge into the column gap. Cross-column redirects
              // keep the center-to-center bezier.
              const sameCol = Math.abs(a.x - b.x) < 1;
              const edgeX = a.x + cw / 2;
              const bow = Math.min(64, 32 + Math.abs(b.y - a.y) * 0.12);
              // REACH: the path stops this far short of the card edge so the terminator lands exactly
              // ON the edge — the arrowhead tip / the circle's near side touch the card (not floating
              // in the gap, not hidden behind it). Slightly less for the circle so its rim kisses the
              // edge; the arrowhead is pulled back by its own length (it's base-anchored, see marker).
              const reach = term === 'route' ? 6.5 : 7.5;
              // Per-endpoint stacking offsets (see edgeSlots): the originator and terminator are each
              // fanned into their own slot on the card edge they touch, so overlapping redirects
              // (incl. a round-trip's out + return) spread apart with "out" always above "return".
              const slot = edgeSlots.get(i);
              // A COLLAPSED pathway is a short (34px) bar. The connector FAN — which spreads several
              // wires apart on a card edge so they don't overlap — would spill above/below such a short
              // bar onto its tightly stacked neighbours ("the flow coming off the card"). So on a
              // collapsed endpoint we DON'T fan: every wire lands at the bar's CENTRE and overlaps,
              // which reads cleanly as "into this bar" instead of drifting onto the next one.
              const sourceCollapsed = !!headInfo.get(e.fromId)?.collapsed;
              const targetCollapsed = e.toId ? !!headInfo.get(e.toId)?.collapsed : false;
              const ay = a.y + (sourceCollapsed ? 0 : (slot?.orig ?? 0)), by = b.y + (targetCollapsed ? 0 : (slot?.term ?? 0));
              // A cross-column redirect runs EDGE to EDGE. Direction-aware: a forward hop (to a deeper
              // column) exits the source's right and enters the target's LEFT; a return (to a shallower
              // column) exits the source's left and enters the target's RIGHT — never wrapping around.
              const forward = b.x >= a.x;
              // The flow handle (port) protrudes past the card's RIGHT edge; a wire leaving the right
              // side emerges FROM the handle, so start it just inside the handle's outer edge (the -3
              // tucks it under the edge so there's no gap between handle and wire). A return exits the
              // left side (no handle there) — the card edge.
              const handleOut = coarse ? 15 : 13;
              // The flow port only protrudes on a full CARD's right edge. A collapsed pathway is a bare
              // bar with no port, AND the scoring aspect hides the port entirely — in both cases a wire
              // touching a right side must land on the card/bar edge itself, not handleOut past it
              // (otherwise the arrow floats in the gap where the port would have been).
              const srcHandleOut = sourceCollapsed || anyAspect ? 0 : handleOut;
              const tgtHandleOut = targetCollapsed || anyAspect ? 0 : handleOut;
              const rightOrigin = edgeX + srcHandleOut - 3;
              const startX = forward ? rightOrigin : a.x - cw / 2;
              let d: string;
              if (straight) {
                // Vertical through-line into the top edge; pull up by reach so the arrowhead tip touches it.
                // Half-heights are per-node so a collapsed pathway (short bar) is met at its own edge.
                d = `M${a.x} ${a.y + halfH(e.fromId)} L${a.x} ${b.y - halfH(e.toId!) - reach}`;
              } else if (sameCol) {
                // Terminate at the handle's OUTER edge, not the card border — the handle sits on the
                // right edge, so a terminator there would hide under it. A collapsed bar has no handle.
                const endX = edgeX + tgtHandleOut + reach;
                d = `M${rightOrigin} ${ay} C${rightOrigin + bow} ${ay}, ${rightOrigin + bow} ${by}, ${endX} ${by}`;
              } else {
                const dir = forward ? 1 : -1;
                // A forward hop enters the target's LEFT edge (no handle). A return enters the RIGHT
                // edge, where the flow handle sits — so aim it at the handle's OUTER edge instead of
                // the card border, otherwise the arrowhead lands hidden under the handle.
                const targetEdgeX = forward ? b.x - cw / 2 : b.x + cw / 2 + tgtHandleOut;
                const endX = targetEdgeX - dir * reach;
                const midX = (startX + endX) / 2;
                d = `M${startX} ${ay} C${midX} ${ay}, ${midX} ${by}, ${endX} ${by}`;
              }
              // Port focus/arm: when a connection is focused from a port, emphasise ITS wire and fade
              // every other line, so it's unmistakable which one. Armed (pending disconnect) additionally
              // recolours the whole wire warning, at full strength — that's what a confirm click drops.
              const pendingDis = edgeMatchesConn(e, pendingDisconnect);
              const isFocusWire = edgeMatchesConn(e, focusConn);
              const dimmed = focusHasWire && !isFocusWire;
              return (
                <g key={i}>
                  <path d={d} stroke={pendingDis ? 'var(--color-ws-warning-fg)' : stroke}
                    strokeWidth={pendingDis ? 3.5 : isFocusWire ? 3 : (lit || wireHot ? 2.5 : 2)} fill='none'
                    markerEnd={marker} opacity={pendingDis || isFocusWire ? 1 : dimmed ? 0.1 : opacity} />
                  {/* Invisible wide hit-stroke so the WIRE itself is hoverable (the SVG layer is
                      otherwise pointer-events-none) — for disconnectable branch wires AND re-targeted
                      correct links. Sits below the cards in z-order, so it only catches the pointer
                      out in the gap. */}
                  {(disc.length > 0 || isCorrectGoto) && !readOnly && (
                    <path d={d} stroke='transparent' strokeWidth={18} fill='none'
                      style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                      onPointerEnter={() => setHoveredEdge(i)}
                      onPointerLeave={() => { setArmedEdge((ar) => (ar === i ? null : ar)); setHoveredEdge((h) => (h === i ? null : h)); }} />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Branch-target highlight — while a drop is set to BRANCH off a card (dropped on its body),
              ring that card in amber so it's clear what you're branching off of. The preview already
              shows the dragged task appearing as a new branch beside it; this just names the parent. */}
          {dropIntent?.kind === 'branch' && (() => {
            const p = layout.placed.get(dropIntent.parentId);
            if (!p) return null;
            const h = headInfo.get(p.id)?.collapsed ? PATHWAY_BAR_H : chOf(p.id);
            return <div key='branch-target' className='pointer-events-none absolute rounded-xl ring-2 ring-ws-text-link' style={{ left: p.x - 3, top: p.y - 3, width: cw + 6, height: h + 6 }} />;
          })()}

          {/* Disconnect target highlight — when a port disconnect is armed, ring the card the wire
              currently lands on (in warning colour, matching the lit wire) so it's clear where the
              connection being dropped points. */}
          {pendingDisconnect && (() => {
            const e = graph.edges.find((ed) => edgeMatchesConn(ed, pendingDisconnect));
            if (!e || !e.toId) return null;
            const p = layout.placed.get(e.toId);
            if (!p) return null;
            const h = headInfo.get(p.id)?.collapsed ? PATHWAY_BAR_H : chOf(p.id);
            return <div key='disconnect-target' className='pointer-events-none absolute rounded-xl ring-2 ring-ws-warning-fg' style={{ left: p.x - 3, top: p.y - 3, width: cw + 6, height: h + 6 }} />;
          })()}

          {/* End cap — a small clip fastened flush to the bottom edge of any task whose through-line
              goes nowhere (dead-end). No top border + matching surface, so it reads as a tab hanging
              off the card. Always visible, so an unintended dead-end is obvious. */}
          {[...layout.placed.values()].filter((p) => isTerminal(p.id) && !headInfo.get(p.id)?.collapsed && p.id !== dragId).map((p) => (
            <div
              key={`end-${p.id}`}
              className='pointer-events-none absolute z-[11] flex -translate-x-1/2 items-center justify-center rounded-b-lg bg-ws-muted px-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-white shadow-sm'
              style={{ left: p.x + cw / 2, top: p.y + chOf(p.id), height: 21 }}
            >
              {t('experience.builder.end')}
            </div>
          ))}

          {/* New Task at every dead-ending pathway — below each End cap (spine end + any branch that
              doesn't return to the spine). Extends THAT pathway (appends a task to its lane). This is
              also the non-drag way to add a task inside a branch. Hidden while wiring/selecting. */}
          {!arming && !anyAspect && !selectMode && !dragId && [...layout.placed.values()].filter((p) => isTerminal(p.id) && !headInfo.get(p.id)?.collapsed).map((p) => (
            <button
              key={`nt-${p.id}`}
              type='button'
              onClick={() => onInsertAfter(p.id)}
              className='absolute z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-lg border border-dashed border-ws-border-strong bg-ws-surface px-4 py-2 text-sm font-medium text-ws-muted shadow-sm hover:border-ws-primary hover:text-ws-primary'
              style={{ left: p.x + cw / 2, top: p.y + chOf(p.id) + 28 }}
            >
              <Plus className='h-4 w-4' />{t('experience.newTask')}
            </button>
          ))}

          {/* Insert-between — a triangle in the LEFT margin of every through-line gap (spine AND each
              branch lane), pointing at the insert point; clicking threads a new task in there and
              auto-rewires the through-line. One per `loose` (sequential-next) edge, so it covers all
              lanes automatically. Touch-friendly hit area. Hidden while wiring/selecting; appending
              past the last card is the prominent "New Task" button below. */}
          {!arming && !anyAspect && !selectMode && graph.edges.map((e) => {
            const a = layout.placed.get(e.fromId); const b = e.toId ? layout.placed.get(e.toId) : null;
            if (!a || !b) return null;
            // Show between consecutive tasks in a SEQUENCE — ONLY a loose `next` through-line link
            // (plain sequential adjacency, same lane, target below). An authored correct-goto is a
            // redirect, not a sequence step: inserting into one migrated the goto and left the insert
            // button displaced onto an unrelated card, so redirects (and cross-lane returns/branches)
            // get no insert point. Append after such a task via its End cap's New Task button instead.
            const seq = a.col === b.col && b.y > a.y && e.kind === 'loose';
            if (!seq) return null;
            const sz = coarse ? 34 : 24; // touch-friendly circle
            return (
              <InsertPoint
                key={`ins-${e.fromId}-${e.toId}`}
                size={sz}
                coarse={coarse}
                x={a.x - 2}
                y={(a.y + chOf(e.fromId) + b.y) / 2}
                onInsert={() => { setOpenInsert(null); onInsertAfter(e.fromId); }}
                onPaste={onPasteAfter ? () => onPasteAfter(e.fromId) : undefined}
                // Same anchor as the other two halves: `e.fromId` IS the card above.
                onPastePathway={onPastePathwayOnto && pastePathwayFits(a.col)
                  ? () => { setOpenInsert(null); onPastePathwayOnto(e.fromId); }
                  : undefined}
                pasteLabel={pasteLabel}
                hostLabel={titleOf(e.fromId)}
                open={openInsert === e.fromId}
                onOpen={() => setOpenInsert(e.fromId)}
                onClose={() => setOpenInsert(null)}
              />
            );
          })}

          {/* On-wire delete ✕ — HTML (the SVG is pointer-events-none), shown at a wire's apex while
              that wire (or this ✕) is hovered. One standard destructive control regardless of the
              wire's category: neutral at rest, red on hover. A branch wire reverts its event(s) to
              retry-in-place; a re-targeted correct link reverts to the sequential `next`. */}
          {!readOnly && graph.edges.map((e, i) => {
            if (hoveredEdge !== i || e.kind === 'loose-thread') return null;
            const a = center(e.fromId); const b = e.toId ? center(e.toId) : null;
            if (!a || !b) return null;
            const disc = edgeDisconnectEvents(e);
            const isCorrectGoto = e.kind === 'locked' && edgeCategory(e) === 'route';
            if (!disc.length && !isCorrectGoto) return null;
            const slot = edgeSlots.get(i); // keep the ✕ on the stacked wire, not the un-offset centre
            const { x, y } = edgeApex(a, b, isAdjacent(e));
            const apexY = y + ((slot?.orig ?? 0) + (slot?.term ?? 0)) / 2;
            // Name the event routing down this wire — shown ONLY on hover, tucked just above the
            // delete control. Read from the edge's provenance so it names the actual source event.
            const label = edgeLabel(e);
            return (
              <div key={`h-${i}`}>
                {label && (
                  <div
                    className={`pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-semibold shadow-md ${
                      armedEdge === i
                        ? 'border-ws-warning-fg bg-ws-warning-bg text-ws-warning-fg' // armed → canonical warning
                        : 'border-ws-border bg-ws-surface font-medium text-ws-text'
                    }`}
                    style={{ left: x, top: apexY - 17 }}
                  >
                    {label}
                  </div>
                )}
                <button
                  type='button'
                  onPointerEnter={() => setHoveredEdge(i)}
                  onPointerLeave={() => { setArmedEdge((ar) => (ar === i ? null : ar)); setHoveredEdge((h) => (h === i ? null : h)); }}
                  onClick={() => {
                    if (armedEdge !== i) { setArmedEdge(i); return; } // first click ARMS, matching the app-wide two-click delete
                    if (isCorrectGoto) onWireEvent(e.fromId, 'correct', 'next');
                    else onDisconnectEvents(e.fromId, disc.map((ev) => ev.id));
                    setArmedEdge(null);
                    setHoveredEdge(null);
                  }}
                  aria-label={t('experience.builder.disconnect')}
                  title={t('experience.builder.disconnect')}
                  className={`absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border bg-ws-surface shadow-md ${
                    armedEdge === i
                      ? 'border-ws-warning-fg text-ws-warning-fg'
                      : 'border-ws-border-strong text-ws-muted hover:border-ws-warning-fg hover:text-ws-warning-fg'
                  }`}
                  style={{ left: x, top: apexY }}
                >
                  <Unlink className='h-4 w-4' />
                </button>
              </div>
            );
          })}

          {/* Cards render at their ACTIVE (preview while dragging) positions and glide there via a CSS
              transition, so dropping's effect previews live. The dragged card is the exception: it
              renders at its REAL slot + the pointer transform, floating over the gap it will fill. */}
          {[...active.layout.placed.values()].map((p) => {
            const task = byId.get(p.id); if (!task) return null;
            const isSpine = !task.pathwayOf;
            const num = spineNum.get(p.id);
            const laneSeq = lanes.get(task.pathwayOf ?? '__spine') ?? [];
            const laneIndex = laneSeq.indexOf(p.id);
            const predId = laneIndex > 0 ? laneSeq[laneIndex - 1] : null; // the card above → indent branches off it
            const outLoose = graph.looseThreads.find((lt) => lt.fromId === p.id);
            const isDragged = dragId === p.id;
            const pos = isDragged ? (real.layout.placed.get(p.id) ?? p) : p;
            // A COLLAPSED pathway shows only its bar (rendered in the overlay pass below) in place of
            // its head; the members are hidden. Skip the card so no task-card renders under the bar.
            if (headInfo.get(p.id)?.collapsed) return null;
            // A branch lane's HEAD is only reachable if some event's goto points at it. If nothing does,
            // the whole lane is unreachable — flag it (a branch with no entrance).
            const noEntrance = !isSpine && !incomingTargets.has(p.id);
            // Past the soft cap. The editor no longer OFFERS a branch this deep, but the cap is a
            // view budget rather than a rule about content, so data can still be here — from an
            // import, a direct API write, or an earlier build. Saying so beats either hiding it or
            // refusing to draw it.
            const tooDeep = p.col > MAX_BRANCH_COL;
            const common = {
              task, x: pos.x, y: pos.y, w: cw, h: chOf(p.id), coarse, snap: narrow, pulse: pulse?.ids.has(p.id) ?? false, pulseKey: pulse?.n ?? 0, /* The panel's hovered row rings its task too — same ring the board's own hover draws, because
                 it means the same thing: this is the one being pointed at. */
              highlighted: hoverTaskId === p.id || highlighted?.taskId === p.id,
              num, isSpine, laneIndex, laneCount: laneSeq.length, noEntrance, tooDeep, looseLabel: outLoose?.label, readOnly,
              animatePos: !reduceMotion && dragId != null && !isDragged, // drag reflow → left/top (unchanged)
              slideAnim: !reduceMotion && layoutAnim, // collapse/expand reflow → GPU transform FLIP
              animateEnter: enterAnim,
              fadingOut: !!task.pathwayId && collapsingPathways.has(task.pathwayId),
              armingActive: arming != null,
              isWireSource: arming?.sourceId === p.id,
              isWireTarget: arming != null && arming.sourceId !== p.id,
              validTargets,
              onHover: () => setHovered(p.id),
              onLeave: () => setHovered((h) => (h === p.id ? null : h)),
              onEdit: () => onEditTask(task),
              onArmWire: (event: TaskEvent) => setArming({ sourceId: p.id, event }),
              onWireDragStart: (event: TaskEvent, sx: number, sy: number) => beginWireDrag(p.id, event, sx, sy),
              onPickTarget: () => { if (arming) { onWireEvent(arming.sourceId, arming.event.id, p.id); setArming(null); } },
              // Disconnect a WIRED event from the port (the keyboard/SR path — the on-wire ✕ is
              // pointer-only). A branch event reverts to retry-in-place; a correct return/goto reverts
              // to the sequential `next`, matching what the ✕ does.
              onDisconnect: (event: TaskEvent) => {
                announce(t('experience.builder.a11y.disconnected', { event: event.kind === 'correct' ? t('task.flow.allCorrect.title') : eventLabel(event, t) }));
                if (event.kind === 'correct') onWireEvent(p.id, 'correct', 'next');
                else onDisconnectEvents(p.id, [event.id]);
              },
              // Arm/disarm the board highlight for a port disconnect (first click arms, second confirms).
              armedDisconnectId: pendingDisconnect?.sourceId === p.id ? pendingDisconnect.eventId : null,
              onArmDisconnect: (eventId: string | null) => setPendingDisconnect(eventId ? { sourceId: p.id, eventId } : null),
              // Hover/focus a Connected row → emphasise its wire + fade the rest (preview, no commit).
              onFocusConnection: (eventId: string | null) => setPortFocusEvent(eventId ? { sourceId: p.id, eventId } : null),
              targetTitleOf: (id: string) => titleOf(id) || t('experience.builder.a11y.untitled'),
              onCopy: () => { announce(t('experience.builder.a11y.copied', { title: titleOf(task.id) })); onCopyTask(task.id); },
              // Offered only when there is something to paste AND it would fit: an item that is
              // present but refuses is worse than one that is not there.
              onPastePathway: onPastePathwayOnto && pastePathwayFits(p.col)
                ? () => onPastePathwayOnto(task.id)
                : undefined,
              selectMode, selected: selectedIds?.has(task.id) ?? false,
              onToggleSelected: () => onToggleSelected?.(task.id),
              onShiftSelect: () => onShiftSelect?.(task.id),
              onDuplicate: () => { announce(t('experience.builder.a11y.duplicated', { title: titleOf(task.id) })); onDuplicateTask(task.id); },
              onDelete: () => { announce(t('experience.builder.a11y.deleted', { title: titleOf(task.id) })); onDeleteTask(task.id); },
              hasBranches: hostIds.has(p.id),
              onMoveUp: () => { announce(t('experience.builder.a11y.movedUp', { title: titleOf(task.id) })); onMoveWithinLane(task.id, -1); },
              onMoveDown: () => { announce(t('experience.builder.a11y.movedDown', { title: titleOf(task.id) })); onMoveWithinLane(task.id, 1); },
              canIndent: predId != null && p.col + 1 <= branchCap,
              onIndent: () => { if (predId) { announce(t('experience.builder.a11y.branchedOff', { title: titleOf(task.id), parent: titleOf(predId) })); onMakeBranchOf(task.id, predId); } },
              canOutdent: !!task.pathwayOf,
              mayWire: branchesAllowed,
              onOutdent: () => { announce(t('experience.builder.a11y.outdented', { title: titleOf(task.id) })); onOutdent(task.id); },
              scoringActive,
              errorsActive,
              problems: problemsByTask?.get(task.id),
              chipMax: chipPlanOf(p.id).max,
              highlightedBlockId: highlighted?.taskId === task.id ? highlighted.blockId : undefined,
              onOpenBlock: onOpenBlock ? (blockId: string) => onOpenBlock(task.id, blockId) : undefined,
              onSetScoring: (patch: { scoringMode: ScoringMode; points: number; penalty: number }) => onSetTaskScoring?.(p.id, patch),
            };
            return <DraggableCard key={p.id} {...common} dragging={isDragged} />;
          })}

          {/* Pathway bars — the pathway name + collapse chevron for each remediation pathway. An
              EXPANDED one floats above its head (in the reserved gap); a COLLAPSED one sits in the
              head's short slot. Either way the bar's top lands at the same y, so it doesn't jump. */}
          {[...headInfo].map(([headId, info]) => {
            // A brand-new pathway being created over the hotspot has the dragged task as its head — its
            // "Name this Pathway" bar would clutter the drop zone, so hide it until the drop lands. But
            // when the dragged task is joining the FRONT of an existing pathway (a lane drop), keep that
            // pathway's bar visible so you still see which pathway you're dropping into.
            if (dragId === headId && dropIntent?.kind === 'branch') return null;
            // The pathway being MOVED floats from its REAL (source) slot + the pointer transform, like a
            // dragged card; every other pathway sits at its ACTIVE (preview) slot and reflows.
            const isDraggedPathway = pathwayDragId != null && info.pathwayId === pathwayDragId;
            const p = (isDraggedPathway ? real.layout.placed.get(headId) : active.layout.placed.get(headId));
            if (!p) return null;
            const y = info.collapsed ? p.y : p.y - PATHWAY_HEAD_GAP;
            return (
              <PathwayBar key={`env-${info.pathwayId}`} id={headId} pathwayId={info.pathwayId} name={info.name} count={info.count} collapsed={info.collapsed}
                x={p.x} y={y} w={cw} readOnly={readOnly} dragging={isDraggedPathway} slideAnim={!reduceMotion && layoutAnim && !isDraggedPathway} blockDelete={pathwaysWithNested.has(info.pathwayId)} scoringActive={scoringActive}
                selectMode={selectMode}
                selected={!!selectedPathwayIds?.has(info.pathwayId)}
                onToggleSelected={() => onTogglePathwaySelected?.(info.pathwayId)}
                onRename={(v) => onRenamePathway(info.pathwayId, v)} onToggleCollapse={() => animatedToggleCollapse(info.pathwayId, info.collapsed)} onCopy={() => onCopyPathway(info.pathwayId)} onDuplicate={() => onDuplicatePathway(info.pathwayId)} onDelete={() => onDeletePathway(info.pathwayId)} onMove={(dir) => movePathway(info.pathwayId, dir)} />
            );
          })}

        </div>
      </div>

      {/* The spine's own end already shows a New Task below its End cap; this footer button is the
          entry point only when the board is empty (no pathways, so no dead-end to hang one on). */}
      {!arming && graph.spine.length === 0 && (
        <div className='mt-2 flex items-center justify-center'>
          <button onClick={onCreateTask} className='flex items-center gap-1.5 rounded-lg border border-dashed border-ws-border-strong bg-ws-surface px-4 py-2 text-sm font-medium text-ws-text hover:border-ws-primary hover:text-ws-primary'>
            <Plus className='h-4 w-4' />{t('experience.newTask')}
          </button>
        </div>
      )}

      {/* Live wire preview while dragging from a port onto a target. Full-viewport fixed overlay in
          client coords (simpler than translating into the scrolled board SVG); pointer-events none
          so the drop hit-test (elementFromPoint) still sees the card underneath. */}
      {wireDrag && createPortal(
        <svg aria-hidden='true' className='pointer-events-none fixed inset-0 z-[60]' width='100%' height='100%'>
          <path
            d={`M${wireDrag.ax} ${wireDrag.ay} C ${(wireDrag.ax + wireDrag.x) / 2} ${wireDrag.ay}, ${(wireDrag.ax + wireDrag.x) / 2} ${wireDrag.y}, ${wireDrag.x} ${wireDrag.y}`}
            fill='none' stroke='var(--color-ws-primary)' strokeWidth={2.5} strokeDasharray='5 4'
          />
          <circle cx={wireDrag.x} cy={wireDrag.y} r={5} fill='var(--color-ws-primary)' />
        </svg>,
        document.body,
      )}

      {/* Tap-to-wire banner: while an event is armed, a fixed bar names it and offers End / Cancel;
          eligible target cards highlight, and tapping one connects. */}
      {arming && (
        <div className='fixed inset-x-0 bottom-4 z-50 flex justify-center px-4'>
          <div className='flex max-w-full items-center gap-3 rounded-full border border-ws-primary/40 bg-ws-surface px-4 py-2 shadow-xl'>
            <EventSymbol kind={arming.event.kind} />
            <span className='min-w-0 truncate text-sm text-ws-text'>
              <span className='font-semibold'>{eventLabel(arming.event, t)}</span>
              <span className='text-ws-muted'> — {t('experience.builder.wireHint')}</span>
            </span>
            <button
              onClick={() => { onWireToNewBranch(arming.sourceId, arming.event.id); setArming(null); }}
              className='flex shrink-0 items-center gap-1 rounded-full border border-ws-border-strong px-3 py-1 text-xs font-semibold text-ws-text hover:border-ws-primary hover:text-ws-primary'
            >
              <Plus className='h-3 w-3' />{t('experience.builder.newBranch')}
            </button>
            {/* Only the correct through-line may END the experience — a wrong/blank/special answer
                ending it would be a surprise, and it has no End-cap rendering. So End is offered only
                when the armed event is the correct event (a branch's correct; spine-correct isn't armable). */}
            {arming.event.kind === 'correct' && (
              <button
                onClick={() => { onWireEvent(arming.sourceId, arming.event.id, 'end'); setArming(null); }}
                className='shrink-0 rounded-full border border-ws-border-strong px-3 py-1 text-xs font-semibold text-ws-text hover:border-ws-primary hover:text-ws-primary'
              >
                {t('experience.builder.end').toUpperCase()}
              </button>
            )}
            <button
              onClick={() => setArming(null)}
              className='shrink-0 rounded-full px-2 py-1 text-xs font-medium text-ws-muted hover:text-ws-text'
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

    </div>
    </DndContext>
  );
}

interface CardProps {
  task: Task; x: number; y: number; w: number; h: number; snap?: boolean; coarse?: boolean; num?: number; isSpine: boolean;
  /** One-shot "look here" — a copy, a paste, an undo putting something back, a jump from the map. */
  pulse?: boolean;
  /** Bumped per pulse so the CSS animation restarts when the same card is pulsed twice. */
  pulseKey?: number;
  /** SUSTAINED, for as long as the overview map node is hovered. A different thing from a pulse:
   *  one says "look here once", this says "this is the one you are pointing at". */
  highlighted?: boolean;
  /** Position + size of this card WITHIN its own lane (spine or branch) — drives the up/down rail. */
  laneIndex: number; laneCount: number;
  /** This branch lane has no entrance (nothing routes to its head) → unreachable; flag it. */
  noEntrance?: boolean;
  /** Deeper than the board offers to create — a warning, never a refusal. */
  tooDeep?: boolean;
  /** `>` can nest this card into a branch of the card above it; `<` can pop it out to the parent lane. */
  canIndent?: boolean; canOutdent?: boolean;
  /** Whether this board offers NEW wires at all — see `branchesAllowed`. The port still appears
   *  where something is already wired, because disconnecting must stay reachable. */
  mayWire?: boolean;
  looseLabel?: string; readOnly: boolean; onHover: () => void; onLeave: () => void;
  /** Tap-to-wire: a wiring gesture is armed somewhere on the board. */
  armingActive?: boolean;
  /** This card is the armed event's source (not a valid target). */
  isWireSource?: boolean;
  /** This card is an eligible wire target — tapping it connects the armed event here. */
  isWireTarget?: boolean;
  /** Ids a goto can validly point at — an event wired outside this set is dangling → treated as open. */
  validTargets?: Set<string>;
  /** Arm one of this card's open events from the port (tap / keyboard). */
  onArmWire?: (event: TaskEvent) => void;
  /** Begin a pointer-drag to wire one of this card's open events (drag). */
  onWireDragStart?: (event: TaskEvent, startX: number, startY: number) => void;
  /** Connect the currently-armed event to this card. */
  onPickTarget?: () => void;
  /** Disconnect a WIRED event (from the port's Connected list) — reverts it (keyboard/SR path). */
  onDisconnect?: (event: TaskEvent) => void;
  /** The event id (of THIS card) whose port disconnect is armed, or null — drives the armed styling. */
  armedDisconnectId?: string | null;
  /** Arm (eventId) / disarm (null) a port disconnect, lighting the matching board wire + target. */
  onArmDisconnect?: (eventId: string | null) => void;
  /** Preview (eventId) / clear (null) a port connection — emphasises its wire, fades the others. */
  onFocusConnection?: (eventId: string | null) => void;
  /** Resolve a target task id to its title, so a wired event can name where it goes. */
  targetTitleOf?: (taskId: string) => string;
  onEdit: () => void; onCopy: () => void; onPastePathway?: () => void; onDuplicate: () => void; onDelete: () => void;
  /** Select mode: the rail becomes a checkbox and the card stops being an editor-open. */
  selectMode?: boolean; selected?: boolean; onToggleSelected?: () => void;
  /** Shift+click from outside the mode: enter it with this card already picked. */
  onShiftSelect?: () => void;
  /** This task HOSTS pathways (some branch hangs off it) → its Delete is blocked (would orphan them). */
  hasBranches?: boolean;
  /** Rail actions: reorder within the lane, and indent/outdent across lanes. */
  onMoveUp: () => void; onMoveDown: () => void; onIndent: () => void; onOutdent: () => void;
  /** Scoring aspect: card shows scoring controls instead of the block strip. */
  scoringActive?: boolean;
  onSetScoring?: (patch: { scoringMode: ScoringMode; points: number; penalty: number }) => void;
  errorsActive?: boolean;
  /** This task's problems, for the errors aspect. Absent means nothing is wrong with it. */
  problems?: TaskProblemSummary;
  /** Chip geometry the card was sized for — see `CHIP_PITCH`. */
  chipMax?: number;
  /** The block the panel's hovered row names, lit on this card. */
  highlightedBlockId?: string;
  onOpenBlock?: (blockId: string) => void;
}

/** Card wrapper: draggable (grip). One kind for every card — the drag itself only supplies the
 * pointer transform for the ACTIVE card; all displacement/reflow is driven by the preview layout. */
function DraggableCard(props: CardProps & { dragging?: boolean; animatePos?: boolean; slideAnim?: boolean; animateEnter?: boolean; fadingOut?: boolean }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: props.task.id });
  return (
    <BuilderCard
      {...props}
      cardRef={setNodeRef}
      dragTransform={transform ? CSS.Translate.toString(transform) : undefined}
      dragHandle={{ attributes, listeners }}
    />
  );
}

function BuilderCard({
  task, x, y, w, h, snap, pulse, pulseKey, highlighted, dragging, animatePos, slideAnim, animateEnter, fadingOut, coarse, num, isSpine, laneIndex, laneCount, noEntrance, tooDeep, canIndent, canOutdent, mayWire = true, looseLabel, readOnly,
  armingActive, isWireSource, isWireTarget, validTargets, onArmWire, onWireDragStart, onPickTarget, onDisconnect, armedDisconnectId, onArmDisconnect, onFocusConnection, targetTitleOf,
  onHover, onLeave, onEdit, onCopy, onPastePathway, onDuplicate, onDelete, selectMode, selected, onToggleSelected, onShiftSelect, hasBranches, onMoveUp, onMoveDown, onIndent, onOutdent,
  scoringActive, onSetScoring, errorsActive, problems, chipMax = 8,
  highlightedBlockId, onOpenBlock,
  cardRef, dragTransform, dragHandle,
}: CardProps & {
  dragging?: boolean;
  animatePos?: boolean;
  slideAnim?: boolean;
  animateEnter?: boolean;
  fadingOut?: boolean;
  cardRef?: (el: HTMLElement | null) => void;
  dragTransform?: string;
  dragHandle?: Pick<ReturnType<typeof useDraggable>, 'attributes' | 'listeners'>;
}) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState(false);
  const closeMenu = () => setMenu(false);
  const [info, setInfo] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Fade in on mount ONLY when appearing due to a pathway expand (animateEnter set on that render);
  // otherwise visible immediately (initial load, reorder, drag). Softens members popping in.
  const [entered, setEntered] = useState(!animateEnter);
  useEffect(() => {
    if (entered) return undefined;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [entered]);
  // GPU-composited slide for pathway collapse/expand (drag keeps its own left/top path via animatePos).
  const flip = useSlideFlip(x, y, !!slideAnim && !dragging);

  // The task's events (see project_experience_builder_connection_model). The port surfaces the
  // ones still OPEN (no branch destination yet); wired ones are visible as connectors instead.
  const events = useMemo(() => deriveTaskEvents(task), [task]);
  // Open = no destination yet, OR wired to a target that no longer exists (a dangling link left by a
  // deleted task) — the latter would otherwise be stuck unavailable forever, so re-surface it.
  const dangling = (e: TaskEvent) => e.wired && e.targetTaskId != null && !!validTargets && !validTargets.has(e.targetTaskId);
  const openBranches = events.branches.filter((e) => !e.endsHere && (!e.wired || dangling(e)));
  // List them in the SAME top-to-bottom order as the task's flow panel: no-answer, then the special
  // rules (in their authored order), then the any-incorrect default — so the two views never disagree.
  const branchOpen = [
    ...openBranches.filter((e) => e.kind === 'noAnswer'),
    ...openBranches.filter((e) => e.kind === 'special'),
    ...openBranches.filter((e) => e.kind === 'incorrect'),
  ];
  // A wired/decided event: a goto with a live target, or an authored End. These populate the port's
  // CONNECTED list so keyboard + screen-reader users can retarget or disconnect a wire (the on-wire ✕
  // is pointer-only). A dangling goto (target deleted) is treated as OPEN instead, so it stays re-linkable.
  const resolved = (e: TaskEvent) => e.endsHere || (e.wired && !dangling(e));
  const branchConnected = events.branches.filter(resolved);
  const connectedList = [
    ...branchConnected.filter((e) => e.kind === 'noAnswer'),
    ...branchConnected.filter((e) => e.kind === 'special'),
    ...branchConnected.filter((e) => e.kind === 'incorrect'),
  ];
  // A branch task's correct (its return / End) is "decided" when it carries a goto or End → show it in
  // CONNECTED (Change / revert to next). A plain through-line `next` stays in OPEN so it can still be
  // pointed elsewhere or Ended. The spine's correct is locked to next, so it's never listed either way.
  const correctResolved = !isSpine && resolved(events.correct);
  const connectedEvents = correctResolved ? [...connectedList, events.correct] : connectedList;
  // Correct in the port: offered on EVERY BRANCH task (last, matching the flow panel's all-correct
  // card) so a branch's correct can always be re-pointed / Ended — incl. breaking a branch sequence
  // that got auto-linked by the correct through-line. NOT offered on the spine: its correct is locked
  // to the sequential next (no skip), and re-pointing the last spine task's correct back up the spine
  // would create an infinite loop (the mastery path just ends there — End cap).
  const openEvents = !isSpine ? (correctResolved ? branchOpen : [...branchOpen, events.correct]) : branchOpen;
  // The flow handle is ALWAYS present (it never disappears) — but it's DISABLED (greyed, inert) when
  // the task has no events available to wire, so it reads as "no flow to author here" without going
  // missing. `openEvents` already folds in the correct event when it's eligible (a terminal task's
  // correct is a live End/return decision), so that counts toward the total shown on the handle.
  /*
   * THE PORT IS HOW A WIRE IS MADE, so it answers to the same capability the drop and the
   * keyboard indent do — otherwise branching is gated everywhere except the one control built
   * for it, which is the control a teacher reaches for first.
   *
   * It survives where something is ALREADY wired, because the menu is also the only way to
   * disconnect: a gate may hide the way into a feature and must never hide the way out.
   */
  const hasPort = mayWire || connectedEvents.length > 0;
  const [port, setPort] = useState(false);
  const portRef = useRef<HTMLButtonElement>(null);
  const portMenuRef = useRef<HTMLDivElement>(null);
  const [portPos, setPortPos] = useState({ top: 0, left: 0 });

  function openMenu() {
    setMenu((m) => !m);
  }
  // Position the panel against the flow port and KEEP it there as the board scrolls (so it stays
  // anchored to its task — you can't scroll away and lose track of which one you're editing). If the
  // port scrolls out of view, close the panel rather than leave it floating over nothing.
  const positionPort = useCallback(() => {
    const r = portRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    // Bound against the BOARD's scroll viewport, not the window — the top menu bars sit ABOVE the board,
    // so close the panel the moment its port scrolls up past the board's top edge (i.e. under the bars)
    // instead of letting it float over them, and keep the panel itself within the board region.
    const board = portRef.current?.closest('[data-board]')?.getBoundingClientRect();
    const topBound = board ? board.top : 0;
    const botBound = board ? board.bottom : vh;
    if (r.bottom < topBound + 4 || r.top > botBound - 4) { setPort(false); return; }
    // Prefer opening to the right of the port; flip to its left (and clamp) when that would spill
    // off a narrow/phone viewport — otherwise the event list is clipped off-screen.
    const PW = 224;
    const PH = 16 + 20 + (openEvents.length ? openEvents.length * 34 : 24) + (connectedEvents.length ? 20 + connectedEvents.length * 34 : 0);
    let left = r.right + 8;
    if (left + PW > vw - 8) left = r.left - PW - 8;
    if (left < 8) left = Math.max(8, vw - PW - 8);
    const top = Math.min(Math.max(topBound, r.top - 6), Math.max(topBound, botBound - PH - 8));
    setPortPos({ top, left });
  }, [openEvents.length, connectedEvents.length]);
  function togglePort() {
    if (!port) positionPort();
    setPort((p) => !p);
  }
  // Keyboard + SR operability for the port panel, mirroring ActionMenu: on open, move focus into the
  // list; Arrow keys rove between rows; Escape or Tab closes and restores focus to the port button; an
  // outside pointerdown closes. Without this the panel was pointer-only (openable by Enter, but focus
  // stayed on the trigger and Escape didn't close), so a wired event couldn't be reached by keyboard.
  useEffect(() => {
    if (!port) return undefined;
    const items = () => [...(portMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])];
    const raf = requestAnimationFrame(() => items()[0]?.focus());
    const back = () => { setPort(false); portRef.current?.focus(); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); back(); return; }
      const list = items(); if (!list.length) return;
      const i = list.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (list[(i + 1) % list.length] ?? list[0])!.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (list[(i - 1 + list.length) % list.length] ?? list[0])!.focus(); }
      else if (e.key === 'Home') { e.preventDefault(); list[0]!.focus(); }
      else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]!.focus(); }
    };
    const onDown = (e: PointerEvent) => {
      const tgt = e.target as Node;
      if (portMenuRef.current?.contains(tgt) || portRef.current?.contains(tgt)) return;
      setPort(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [port]);
  // When this card's port closes while a disconnect was still armed, drop the board highlight.
  useEffect(() => {
    if (!port && armedDisconnectId != null) onArmDisconnect?.(null);
  }, [port]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keep the open panel glued to the flow port as the board scrolls/resizes (capture, to catch the
  // board's inner scroll container whose scroll events don't bubble).
  useEffect(() => {
    if (!port) return undefined;
    const onMove = () => positionPort();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => { window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove); };
  }, [port, positionPort]);

  // What a click on the card (body OR title) does. While a wire is armed the WHOLE card is a
  // connect surface, never an open control: an eligible target connects, the source or an
  // ineligible card is a no-op (Esc/Cancel exits). Only with nothing armed does a click open.
  function activate() {
    // Select mode owns the card: clicking it scopes rather than opens. Shift+click enters the mode
    // from outside it, so the two never have to disagree about what a plain click means.
    if (selectMode) { onToggleSelected?.(); return; }
    /*
     * The scoring aspect picks too, and needs no mode to do it.
     *
     * A card there is a scoring surface rather than an editor-open, so a click meant nothing — the
     * one gesture the board has was going spare on the one surface with an obvious use for it.
     * Clicking picks, and the panel offers "Apply to N selected" beside the individual controls and
     * Apply to Main Flow.
     */
    if (scoringActive) { onToggleSelected?.(); return; }
    if (armingActive) { if (isWireTarget) onPickTarget?.(); return; }
    onEdit();
  }
  // Body click → activate, unless it landed on a real control (the rail/⋯/debug/port button, or the
  // title, which handle their own click) or the drag grip.
  function handleCardClick(e: React.MouseEvent) {
    // `input` as well as `button`: the scoring aspect's card body is steppers, and typing a value
    // must not also pick the card. It used to be covered by a `data-noedit` on the whole strip,
    // which was too broad — it swallowed the click meant for the card itself.
    if ((e.target as HTMLElement).closest('button, input, [data-noedit]')) return;
    // Shift+click is the desktop way INTO select mode, and lands in exactly the state the toggle
    // produces — so there is still one selection system rather than two that must agree. Touch has
    // no modifier, which is why the toggle is the primary route rather than an accelerator.
    if (e.shiftKey && !scoringActive && !armingActive) { onShiftSelect?.(); return; }
    activate();
  }

  const showRail = !!dragHandle;
  // Rail controls, shared between the compact (touch) column and the d-pad (mouse/keyboard) layout.
  const railBtn = `flex items-center justify-center rounded-md text-ws-muted hover:bg-ws-hover disabled:opacity-20 [&:focus:not(:focus-visible)]:outline-none ${coarse ? 'h-10 w-10' : 'h-6 w-6'}`;
  const railIcon = coarse ? 'h-5 w-5' : 'h-4 w-4';
  const upBtn = (
    <button type='button' disabled={laneIndex <= 0} aria-label={t('task.moveUp')} className={railBtn}
      onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onMoveUp(); }}
      onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onMoveUp(); }}>
      <ChevronUp className={railIcon} />
    </button>
  );
  const downBtn = (
    <button type='button' disabled={laneIndex >= laneCount - 1} aria-label={t('task.moveDown')} className={railBtn}
      onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onMoveDown(); }}
      onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onMoveDown(); }}>
      <ChevronDown className={railIcon} />
    </button>
  );
  // The grip is the drag handle AND the keyboard mover: with it focused, ↑/↓ reorder within the lane
  // and ←/→ move the task ACROSS lanes (← out to the parent lane, → into a branch of the card above).
  // That's how a keyboard user nests/un-nests — no cramped inline </> buttons needed.
  // The grip is the card's primary focus stop, so its accessible name carries the task's identity and
  // place (title + "task N" on the spine, or "branch task" in a lane) — otherwise a screen-reader user
  // hears only "drag to move" with no idea which card it is.
  const gripTitle = task.title || t('experience.builder.a11y.untitled');
  const gripLabel = isSpine
    ? t('experience.builder.a11y.cardGripSpine', { title: gripTitle, num: num ?? 0 })
    : t('experience.builder.a11y.cardGripBranch', { title: gripTitle });
  const grip = dragHandle ? (
    // dnd-kit's spread attributes supply role="button" + tabIndex; eslint can't see them past the spread.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div {...dragHandle.attributes} {...dragHandle.listeners} data-noedit aria-label={gripLabel}
      title={t('experience.builder.reorderHint')}
      /* Built from what the handler will ACT on, not from the full rail. Advertising ArrowRight
         on a board where branching is gated promises a key that does nothing, which is a worse
         answer for a screen-reader user than not offering it — they would be left believing the
         indent failed. Same for ArrowLeft on a card with nothing to outdent to. */
      aria-keyshortcuts={['ArrowUp', 'ArrowDown', ...(canOutdent ? ['ArrowLeft'] : []), ...(canIndent ? ['ArrowRight'] : [])].join(' ')}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp') { e.preventDefault(); onMoveUp(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); onMoveDown(); }
        else if (e.key === 'ArrowLeft' && canOutdent) { e.preventDefault(); onOutdent(); }
        else if (e.key === 'ArrowRight' && canIndent) { e.preventDefault(); onIndent(); }
      }}
      className={`flex cursor-grab touch-none items-center justify-center rounded text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-ws-primary ${coarse ? 'h-10 w-10' : 'h-6 w-6'}`}>
      <GripVertical className={railIcon} />
    </div>
  ) : null;

  return (
    // Clicking anywhere on the card opens the editor (matching the task's block cards). The rail,
    // ⋯ menu and debug icon stop propagation so they don't trigger it; the title stays a real
    // button, so keyboard/AT users have a focusable "open" control — hence the a11y disable here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      ref={cardRef}
      data-card={task.id}
      onClick={handleCardClick}
      className={`group hover-card absolute rounded-xl border bg-ws-surface ${
        selectMode || scoringActive ? 'cursor-pointer'
        : isWireTarget ? 'cursor-copy ring-2 ring-ws-primary ring-offset-1 ring-offset-ws-bg shadow-lg'
        : isWireSource ? 'ring-2 ring-ws-primary/40'
        : armingActive ? 'cursor-default'
        : 'cursor-pointer'
      } ${isWireTarget || isWireSource ? '' : 'shadow-[0px_1px_2px_0px_#0000000D]'} ${isSpine ? 'border-ws-border' : 'border-l-[3px] border-l-ws-primary border-ws-border'}`}
      style={{ left: x, top: y, width: w, height: h, scrollSnapAlign: snap ? 'center' : undefined, transform: dragTransform ?? flip.transform, opacity: (entered && !fadingOut) ? (dragging ? 0.4 : (armingActive && !isWireTarget && !isWireSource ? 0.6 : 1)) : 0, transition: flip.active ? (flip.playing ? `transform 260ms ${SLIDE_EASE}, opacity 200ms ease` : 'opacity 200ms ease') : `${animatePos ? `left 260ms ${SLIDE_EASE}, top 260ms ${SLIDE_EASE}, ` : ''}opacity 200ms ease`, zIndex: dragging ? 40 : undefined }}
      onPointerEnter={onHover}
      onPointerLeave={onLeave}
      onTransitionEnd={(e) => { if (e.propertyName === 'transform' && e.target === e.currentTarget) flip.clear(); }}
    >
      {/* Sustained highlight while the overview map points at this card. Separate overlay from the
          pulse below, because one persists and the other is a single 700ms gesture. */}
      {/* One highlight, reused. A selected card lights the SAME overlay the overview map's hover
          uses; adding a second ring to the card's border stacked two rings of different radii and
          read as thick and gappy. */}
      <div aria-hidden='true' className='pointer-events-none absolute inset-0 rounded-xl shadow-lg ring-2 ring-ws-primary transition-opacity duration-200 ease-out' style={{ opacity: highlighted || ((selectMode || scoringActive) && selected) ? 1 : 0 }} />
      {/*
        The shared pulse, keyed so a second pulse on the same card restarts the animation rather
        than doing nothing. Was a 500ms-in / 500ms-out opacity transition held for 1200ms — no
        attack at all, which read as a state the card had entered rather than a pointer at it.
      */}
      {pulse && <div key={pulseKey} aria-hidden='true' className='agavi-pulse pointer-events-none absolute inset-0 rounded-xl' />}
      {/* Select mode reads by CONTRAST: the board dims and what is picked stays lit. It makes the
          mode unmistakable from the first glance — the objection to a checkbox per card was that it
          adds furniture to say what emptiness already says. */}
      {selectMode && (
        <div aria-hidden='true' className={`pointer-events-none absolute inset-0 rounded-xl transition-opacity ${
          selected ? 'opacity-0' : 'bg-ws-bg/60 opacity-100'
        }`} />
      )}
      <div className='flex h-full items-stretch'>
        {/* Reorder + nesting rail — up / grip / down, always visible (mirrors the task's block-list
            nav). The grip is the universal drag handle (mouse, touch, pen); with it focused, keyboard
            ↑/↓ reorder within the lane and ←/→ nest across branch lanes. */}
        {/* Kept and DISABLED in select mode rather than removed: taking the column away reflowed
            the card and made the title jump as the mode toggled. Selection is shown by the card
            itself — the board dims and only what is picked stays lit — so nothing replaces it. */}
        {showRail && (

          <div className={`flex shrink-0 flex-col items-center justify-center ${coarse ? 'gap-1 pl-1.5' : 'gap-0.5 pl-1'}`}>
            {selectMode
              ? <span aria-hidden='true' className='pointer-events-none opacity-30'>{upBtn}{grip}{downBtn}</span>
              : <>{upBtn}{grip}{downBtn}</>}
          </div>
        )}

        {/* `pr-8` CLEARS WHAT IS PINNED TO THE RIGHT EDGE for the whole body — the ⋯ at the top
            and the info button at the bottom corner, 32px between them. The points chip needs more
            again, but it is pinned at the TOP, so that extra belongs to the title row alone: on the
            body it also squeezed the chip strip, which wrapped a row the card was not grown for and
            lost it over the bottom edge. */}
        <div className='relative flex min-w-0 flex-1 flex-col justify-center gap-1 py-2 pl-2.5 pr-8'>
          {/* ⋯ menu — upper right. Disabled while wiring: the whole card is a connect surface then,
              and the menu's Edit/Duplicate/etc. would be an "open" escape hatch. Hidden in the scoring
              aspect (a focus mode — structural actions live in the default Structure view). */}
          {!scoringActive && !selectMode && (
            <button ref={btnRef} onClick={openMenu} disabled={armingActive} className='absolute right-4 top-1.5 flex h-6 w-6 items-center justify-center rounded-md text-ws-muted hover:bg-ws-hover disabled:pointer-events-none disabled:opacity-30' aria-label={t('common.more')}>
              <MoreHorizontal className='h-4 w-4' />
            </button>
          )}
          {/* Whisper-quiet points indicator (Structure mode only) — shows a graded task's worth at a
              glance without entering the scoring aspect. Sits left of the ⋯ menu.
              
              The glyph is the task's MODE, not a trophy. The chip only renders when a task carries
              points, which is exactly the two modes that do — milestone and penalty — and a trophy
              said nothing about which. `SCORING_MODE_ICON` is the same symbol the scoring panel's
              selector and the card cycler already teach, so it costs no new vocabulary and no extra
              space. A penalty's size stays in the tooltip: the chip answers "what is this worth",
              and the ladder belongs where there is room to read it. */}
          {!scoringActive && (task.points ?? 0) > 0 && (() => {
            const mode = inferScoringMode(task);
            const ModeIcon = SCORING_MODE_ICON[mode];
            return (
              <span className='absolute right-10 top-2 flex items-center gap-0.5 text-[10px] font-semibold text-ws-muted'
                title={mode === 'penalty'
                  ? t('experience.scoring.worthPenalty', { count: task.points, penalty: task.penalty ?? 0 })
                  : t('experience.scoring.pointsWorth', { count: task.points })}>
                <ModeIcon aria-hidden='true' className='h-3 w-3' />{task.points}
              </span>
            );
          })()}
          {/* heritage debug tooltip — kept intentionally; lower right */}
          <button onMouseEnter={() => setInfo(true)} onMouseLeave={() => setInfo(false)} className='absolute bottom-1.5 right-2 text-ws-muted opacity-40 hover:opacity-100' aria-label={t('experience.debug.title')}>
            <Info className='h-3.5 w-3.5' />
            {info && (
              <span className='absolute bottom-5 right-0 z-50 w-56 rounded-md border border-ws-border bg-ws-surface p-2 text-left shadow-md'>
                <span className='mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ws-muted'>{t('experience.debug.title')}</span>
                <span className='block break-all font-mono text-[10px] text-ws-text'>{t('experience.debug.id')}: {task.id}</span>
                <span className='block break-all font-mono text-[10px] text-ws-text'>{t('experience.debug.heritage')}: {task.heritageId ?? t('experience.debug.original')}</span>
                <span className='block break-all font-mono text-[10px] text-ws-text'>{t('experience.debug.author')}: {task.authorId ?? '—'}</span>
              </span>
            )}
          </button>
          {/* number + title — number centred on the title's FIRST line (stays there when the
              title wraps to a second line, via items-start + a line-height-matched box). The extra
              right padding is this row's alone: the points chip it clears is pinned beside the ⋯,
              level with the title, and nothing below it is in the chip's way. */}
          <div className={`flex items-start gap-2 ${
            !scoringActive && (task.points ?? 0) > 0 ? 'pr-[44px]' : ''}`}>
            {(num != null || noEntrance || tooDeep) && (
              <span className='flex h-[1.375em] shrink-0 items-center text-[15px]'>
                {/* Unreachable outranks too-deep: a lane nothing can enter is a broken experience,
                    a deep one is only an awkward board. One badge slot, most severe wins. */}
                {noEntrance || tooDeep ? (
                  // An amber warning sits where the spine number would.
                  <span className='flex h-5 w-5 items-center justify-center rounded-full bg-ws-warning-fg text-white'
                    title={noEntrance ? t('experience.builder.noEntrance') : t('experience.builder.tooDeep')}
                    aria-label={noEntrance ? t('experience.builder.noEntrance') : t('experience.builder.tooDeep')}>
                    <TriangleAlert className='h-3 w-3' />
                  </span>
                ) : (
                  <span className='flex h-5 w-5 items-center justify-center rounded-full bg-ws-primary text-[10px] font-bold text-white'>{num}</span>
                )}
              </span>
            )}
            <button onClick={activate} className={`line-clamp-2 min-w-0 flex-1 cursor-[inherit] text-left text-[15px] font-semibold leading-snug text-ws-text focus-visible:underline focus:outline-none ${armingActive ? '' : 'group-hover:text-ws-primary'}`} title={task.title}>
              {task.title || t('nav.newExperience')}
            </button>
          </div>
          {/* block composition fingerprint */}
          {scoringActive
            ? <CardScoringBody task={task} readOnly={readOnly} coarse={coarse} onSet={(patch) => onSetScoring?.(patch)} />
            : errorsActive ? <ProblemStrip blocks={task.blocks} problems={problems}
                max={chipMax}
                highlightedBlockId={highlightedBlockId} onOpenBlock={onOpenBlock} />
            : <BlockStrip blocks={task.blocks} />}
          {noEntrance && (
            <span className='flex items-center gap-1 pl-7 text-[10.5px] font-semibold text-ws-warning-fg'>
              <TriangleAlert className='h-3 w-3' />{t('experience.builder.noEntrance')}
            </span>
          )}
          {looseLabel && (
            <span className='flex items-center gap-1 pl-7 text-[10.5px] font-semibold text-ws-warning-fg'>
              <TriangleAlert className='h-3 w-3' />{looseLabel} → {t('common.unresolved')}
            </span>
          )}
        </div>
      </div>

      {/* Connection port (right edge) — the entry point for wiring: reveals the task's open events
          to connect. Disabled while another wire is armed (you've already chosen a source event).
          Only for answerable tasks. Hidden in the scoring aspect (you're not wiring there). */}
      {hasPort && !scoringActive && !selectMode && (
        <button
          ref={portRef}
          onClick={(e) => { e.stopPropagation(); togglePort(); }}
          disabled={armingActive || (openEvents.length === 0 && connectedEvents.length === 0)}
          aria-haspopup='menu'
          aria-expanded={port}
          aria-label={[
            t('experience.builder.eventsPort'),
            openEvents.length ? t('experience.builder.eventsPortCount', { count: openEvents.length }) : null,
            connectedEvents.length ? t('experience.builder.eventsConnectedCount', { count: connectedEvents.length }) : null,
          ].filter(Boolean).join(', ')}
          className={`absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-md border shadow-sm transition-colors disabled:pointer-events-none disabled:border-ws-border disabled:text-ws-muted/40 disabled:shadow-none ${coarse ? 'right-[-15px] h-[60px] w-[30px]' : 'right-[-13px] h-[54px] w-[26px]'} ${port ? 'border-ws-primary bg-ws-primary text-white' : 'border-ws-border bg-ws-surface text-ws-muted hover:border-ws-primary hover:text-ws-primary'}`}
        >
          {/* The count of events still available to wire — open branches PLUS the correct when it's
              an eligible decision (terminal task). 0 when everything's connected (the port still opens,
              to review/disconnect); centred + muted, informational, not a notification. */}
          <span className={`font-semibold tabular-nums ${coarse ? 'text-sm' : 'text-[13px]'}`}>{openEvents.length}</span>
        </button>
      )}

      {port && createPortal(
        // data-noedit: this panel is portaled to <body>, so a click on its empty area would otherwise
        // bubble through the React tree to the card's onClick and open the editor. The card's click
        // guard skips anything inside a [data-noedit] DOM ancestor, and this div is one.
        <div ref={portMenuRef} role='menu' aria-label={t('experience.builder.eventsPort')} data-noedit style={{ top: portPos.top, left: portPos.left }}
          className={`fixed ${POPUP_LEVEL} w-56 rounded-xl border border-ws-border-strong bg-ws-surface p-2 shadow-xl`}>
          {/* AVAILABLE — events with no destination yet: click/Enter arms tap-to-wire, drag pulls a
              wire. Always shown (with a ghosted "None" when empty) so the two sections read consistently. */}
          <div className='px-1.5 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-ws-muted'>{t('experience.builder.eventsOpen')}</div>
          {openEvents.length > 0 ? openEvents.map((ev) => (
            <button
              key={ev.id}
              type='button'
              role='menuitem'
              tabIndex={-1}
              disabled={readOnly}
              onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); setPort(false); onWireDragStart?.(ev, e.clientX, e.clientY); }}
              onClick={(e) => { e.stopPropagation(); setPort(false); onArmWire?.(ev); }}
              className='flex w-full touch-none items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left hover:bg-ws-hover focus:bg-ws-hover focus:outline-none disabled:opacity-50'
            >
              <EventSymbol kind={ev.kind} />
              <span className='flex-1 text-[12.5px] font-medium text-ws-text'>{eventLabel(ev, t)}</span>
            </button>
          )) : (
            <div className='px-1.5 pb-1 pl-6 text-[12.5px] italic text-ws-muted/70'>{t('experience.builder.eventsNone')}</div>
          )}
          {/* CONNECTED — events already wired: symbol + name + a two-click Disconnect. Arming the
              disconnect lights the matching wire + its target on the board (see pendingDisconnect), so
              it's clear exactly what a confirm click drops. To retarget, disconnect then wire anew. */}
          {connectedEvents.length > 0 && (
            <>
              <div className='mt-1.5 border-t border-ws-border px-1.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-ws-muted'>{t('experience.builder.eventsConnected')}</div>
              {connectedEvents.map((ev) => {
                // Destination is kept for the screen-reader label only — it's visible on the board.
                const dest = ev.endsHere ? t('experience.builder.end') : (targetTitleOf?.(ev.targetTaskId ?? '') ?? '');
                const evName = eventLabel(ev, t);
                const armed = armedDisconnectId === ev.id;
                return (
                  // Hovering/focusing a row previews its wire on the board (fades the rest). Leaving the
                  // row clears that preview AND disarms any pending disconnect — the on-wire ✕ / two-click
                  // delete motif: move away and it's cancelled.
                  <div
                    key={ev.id}
                    role='group'
                    aria-label={evName}
                    onPointerEnter={() => onFocusConnection?.(ev.id)}
                    onPointerLeave={() => { onFocusConnection?.(null); if (armed) onArmDisconnect?.(null); }}
                    className={`flex items-center gap-2 rounded-md px-1.5 py-1 ${armed ? 'bg-ws-warning-bg ring-1 ring-ws-warning-fg' : ''}`}
                  >
                    <EventSymbol kind={ev.kind} />
                    <span className={`min-w-0 flex-1 truncate text-[12.5px] font-medium ${armed ? 'text-ws-warning-fg' : 'text-ws-text'}`}>{evName}</span>
                    {/* Disconnect — two-click armed; the first click highlights the wire on the board. */}
                    <button
                      type='button'
                      role='menuitem'
                      tabIndex={-1}
                      disabled={readOnly}
                      onClick={(e) => { e.stopPropagation(); if (!armed) { onArmDisconnect?.(ev.id); return; } onDisconnect?.(ev); onArmDisconnect?.(null); setPort(false); }}
                      onFocus={() => onFocusConnection?.(ev.id)}
                      onBlur={() => { onFocusConnection?.(null); if (armed) onArmDisconnect?.(null); }}
                      aria-label={armed ? t('experience.builder.disconnectConfirm') : t('experience.builder.a11y.disconnectEvent', { event: evName, target: dest })}
                      title={armed ? t('experience.builder.disconnectConfirm') : t('experience.builder.disconnect')}
                      className={`flex shrink-0 items-center justify-center rounded-md focus:outline-none focus-visible:ring-1 focus-visible:ring-ws-primary disabled:opacity-50 ${coarse ? 'h-9 w-9' : 'h-7 w-7'} text-ws-warning-fg ${armed ? '' : 'hover:bg-ws-warning-bg'}`}
                    >
                      <Unlink className='h-4 w-4' aria-hidden='true' />
                    </button>
                  </div>
                );
              })}
            </>
          )}
          <span className='sr-only' role='status' aria-live='assertive'>{armedDisconnectId ? t('experience.builder.disconnectConfirm') : ''}</span>
        </div>,
        document.body,
      )}

      <ActionMenu open={menu} onClose={closeMenu} anchorRef={btnRef} onCopy={onCopy} onPastePathway={onPastePathway} onDuplicate={onDuplicate} onDelete={onDelete} blockDelete={!!hasBranches} />
    </div>
  );
}
